import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyAPI, branchAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyCreate.css';

const PropertyCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1);
  const [saving, setSaving] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [dropdowns, setDropdowns] = useState({
    cities: [],
    areas: [],
    districts: [],
    nationalities: [],
    identities: [],
    banks: [],
    realEstateTypes: [],
    realEstateUsages: [],
    unitTypes: [],
    unitUsages: [],
    facilities: [],
    unitFacilities: [],
    branches: []
  });

  // Tab 1: Property Information
  const [tab1Data, setTab1Data] = useState({
    nameEng: '',
    nameAr: '',
    ownerDocumentType: '1',
    titleDeed: '',
    identityNumber: '',
    crNumber: '',
    issueDate: '',
    placeTypeCityId: '',
    branchId: ''
  });

  // Tab 2: Ownership Information
  const [ownerships, setOwnerships] = useState([]);
  const [showOwnershipModal, setShowOwnershipModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingOwnershipIndex, setEditingOwnershipIndex] = useState(null);
  const [ownershipFormSubmitted, setOwnershipFormSubmitted] = useState(false);
  const [ownershipFormData, setOwnershipFormData] = useState({
    id: null, // Preserve original ownership ID when editing
    ownershipNameAr: '',
    ownershipNameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileNumberCountryCode: '',
    mobileNumber: '',
    emailId: '',
    bankId: '',
    bankAccountNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: '',
    isAgent: false,
    agentNumber: '',
    ownerId: ''
  });

  // Tab 3: Property Details
  const [tab3FormSubmitted, setTab3FormSubmitted] = useState(false);
  const [tab3Data, setTab3Data] = useState({
    municipalLicenseNumber: '',
    realEstateTypeId: '',
    realEstateStatus: '',
    realEstateUsageId: '',
    realEstateNumber: '',
    unitFloor: '',
    realEstateAge: '',
    unitTotalNumber: '',
    floorsTotalNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: '',
    isPartOfCompound: false,
    selectedFacilities: {} // { facilityId: quantity }
  });

  // Tab 4: Units Details
  const [units, setUnits] = useState([]);
  const [deletedUnitIds, setDeletedUnitIds] = useState([]); // Track deleted unit IDs
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnitIndex, setEditingUnitIndex] = useState(null);
  const [unitFormSubmitted, setUnitFormSubmitted] = useState(false);
  const [unitFormData, setUnitFormData] = useState({
    id: null, // Preserve original unit ID when editing
    unitNumber: '',
    unitTypeId: '',
    unitUsageId: '',
    floorNumber: '',
    unitArea: '',
    numberOfRooms: '',
    establishedDate: '',
    unitDirection: '',
    unitFinishing: '',
    unitFrontLength: '',
    unitLength: '',
    unitWidth: '',
    unitHeight: '',
    includeMezzanine: false,
    furnished: false,
    kitchenCabinets: false,
    electricityMeter: '',
    gasMeterNo: '',
    waterMeter: '',
    countOfParking: '',
    selectedFacilities: {} // { facilityId: quantity }
  });

  // Tab 5: Attachments
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState(null);
  const [attachmentFormData, setAttachmentFormData] = useState({
    name: '',
    file: null,
    filePath: '' // For existing attachments
  });

  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const [propertyDropdownsResponse, branchesResponse] = await Promise.all([
        propertyAPI.getDropdowns(),
        // Load a large page of branches for use in dropdown (not paged in UI)
        branchAPI.getAll('', 1, 1000)
      ]);

      const data = propertyDropdownsResponse.data || {};
      const branchesData = (branchesResponse.data && (branchesResponse.data.items || branchesResponse.data)) || [];
      // Handle both camelCase and PascalCase API responses
      const dropdownData = {
        cities: data.Cities || data.cities || [],
        areas: data.Areas || data.areas || [],
        districts: data.Districts || data.districts || [],
        nationalities: data.Nationalities || data.nationalities || [],
        identities: data.IdentityTypes || data.identityTypes || data.Identities || data.identities || [],
        banks: data.Banks || data.banks || [],
        realEstateTypes: data.RealEstateTypes || data.realEstateTypes || [],
        realEstateUsages: data.RealEstateUsages || data.realEstateUsages || [],
        unitTypes: data.UnitTypes || data.unitTypes || [],
        unitUsages: data.UnitUsages || data.unitUsages || [],
        facilities: data.FacilitiesAndAmenities || data.facilitiesAndAmenities || [],
        unitFacilities: data.UnitFacilitiesAndAmenities || data.unitFacilitiesAndAmenities || [],
        branches: branchesData
      };
      console.log('Loaded dropdowns:', {
        unitFacilitiesCount: dropdownData.unitFacilities.length,
        unitFacilities: dropdownData.unitFacilities,
        branchesCount: dropdownData.branches.length
      });
      setDropdowns(dropdownData);
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
      // Keep default empty arrays on error
    }
  };

  const validateTab1 = () => {
    const newErrors = {};
    if (!tab1Data.nameEng.trim()) newErrors.nameEng = t('property.create.errors.nameEngRequired');
    if (!tab1Data.nameAr.trim()) newErrors.nameAr = t('property.create.errors.nameArRequired');
    if (!tab1Data.ownerDocumentType) newErrors.ownerDocumentType = t('property.create.errors.ownerDocumentTypeRequired');
    if (!tab1Data.titleDeed.trim()) newErrors.titleDeed = t('property.create.errors.titleDeedRequired');
    
    // Conditional validation based on ownerDocumentType
    if (tab1Data.ownerDocumentType === '1' && !tab1Data.identityNumber.trim()) {
      newErrors.identityNumber = t('property.create.errors.identityNumberRequired');
    }
    if (tab1Data.ownerDocumentType === '2' && !tab1Data.crNumber.trim()) {
      newErrors.crNumber = t('property.create.errors.crNumberRequired');
    }
    
    if (!tab1Data.issueDate) newErrors.issueDate = t('property.create.errors.issueDateRequired');
    if (!tab1Data.placeTypeCityId) newErrors.placeTypeCityId = t('property.create.errors.placeTypeCityIdRequired');
    if (!tab1Data.branchId) newErrors.branchId = t('property.create.errors.branchIdRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Tab 3 (Property Details) - all mandatory except postalCode and facilities
  const validateTab3 = () => {
    const errors = [];
    
    if (!tab3Data.municipalLicenseNumber.trim()) {
      errors.push(t('property.create.errors.municipalLicenseRequired', 'Municipal License Number is required'));
    }
    if (!tab3Data.realEstateTypeId) {
      errors.push(t('property.create.errors.realEstateTypeRequired', 'Property Type is required'));
    }
    if (!tab3Data.realEstateStatus) {
      errors.push(t('property.create.errors.realEstateStatusRequired', 'Property Status is required'));
    }
    if (!tab3Data.realEstateUsageId) {
      errors.push(t('property.create.errors.realEstateUsageRequired', 'Property Usage is required'));
    }
    if (!tab3Data.realEstateNumber.trim()) {
      errors.push(t('property.create.errors.realEstateNumberRequired', 'Property Number is required'));
    }
    if (!tab3Data.unitFloor) {
      errors.push(t('property.create.errors.unitFloorRequired', 'Unit Floor is required'));
    }
    if (!tab3Data.realEstateAge) {
      errors.push(t('property.create.errors.realEstateAgeRequired', 'Property Age is required'));
    }
    if (!tab3Data.unitTotalNumber) {
      errors.push(t('property.create.errors.unitTotalNumberRequired', 'Total Units Number is required'));
    }
    if (!tab3Data.floorsTotalNumber) {
      errors.push(t('property.create.errors.floorsTotalNumberRequired', 'Total Floors Number is required'));
    }
    if (!tab3Data.areaId) {
      errors.push(t('property.create.errors.tab3AreaRequired', 'Area is required'));
    }
    if (!tab3Data.cityId) {
      errors.push(t('property.create.errors.tab3CityRequired', 'City is required'));
    }
    if (!tab3Data.districtId) {
      errors.push(t('property.create.errors.tab3DistrictRequired', 'District is required'));
    }
    if (!tab3Data.streetName.trim()) {
      errors.push(t('property.create.errors.tab3StreetNameRequired', 'Street Name is required'));
    }
    if (!tab3Data.buildingNumber.trim()) {
      errors.push(t('property.create.errors.tab3BuildingNumberRequired', 'Building Number is required'));
    }
    if (!tab3Data.additionalNumber.trim()) {
      errors.push(t('property.create.errors.tab3AdditionalNumberRequired', 'Additional Number is required'));
    }
    // postalCode is optional - no validation needed
    // selectedFacilities is optional - no validation needed
    
    return errors;
  };

  // Check if a tab can be accessed (without triggering validation)
  const canAccessTab = (tabNumber) => {
    if (tabNumber === 1) return true; // Tab 1 is always accessible
    
    // For Tab 2 and onwards, property must be created (Tab 1 must be saved)
    // We check propertyId as it's set only after Tab 1 is successfully saved
    return propertyId !== null;
  };

  // Handle tab click with validation
  const handleTabClick = async (tabNumber) => {
    // Allow going back to previous tabs without saving
    if (tabNumber < activeTab) {
      setActiveTab(tabNumber);
      return;
    }
    
    // If clicking on the same tab, do nothing
    if (tabNumber === activeTab) {
      return;
    }
    
    // If going to a next tab, save current tab first
    if (tabNumber > activeTab) {
      if (activeTab === 1) {
        if (!validateTab1()) {
          setErrorMessage(t('property.create.messages.completeRequiredFields'));
          setShowErrorModal(true);
          return;
        }
        const saved = await saveTab1();
        if (!saved) return;
      } else if (activeTab === 2) {
        if (!propertyId) {
          setErrorMessage(t('property.create.messages.completeTab1First'));
          setShowErrorModal(true);
          return;
        }
        // Validate that at least one owner or agent is added
        if (ownerships.length === 0) {
          setErrorMessage(t('property.create.errors.ownershipRequired', 'Please add at least one owner or agent'));
          setShowErrorModal(true);
          return;
        }
        // Save ownerships via Tab2 endpoint
        try {
          setSaving(true);
          // Format ownerships data (same logic as handleNext)
          const formattedOwnerships = ownerships.map(ownership => {
            let ownerIdValue = null;
            if (ownership.ownerId) {
              if (Array.isArray(ownership.ownerId)) {
                const firstVal = ownership.ownerId[0];
                if (firstVal && firstVal.toString().trim() !== '') {
                  const parsed = parseInt(firstVal.toString());
                  ownerIdValue = isNaN(parsed) ? null : parsed;
                }
              } else if (typeof ownership.ownerId === 'string') {
                const trimmed = ownership.ownerId.trim();
                if (trimmed !== '') {
                  const parsed = parseInt(trimmed);
                  ownerIdValue = isNaN(parsed) ? null : parsed;
                }
              } else if (typeof ownership.ownerId === 'number') {
                ownerIdValue = isNaN(ownership.ownerId) ? null : ownership.ownerId;
              }
            }
            
            const parseId = (value) => {
              if (!value) return null;
              if (Array.isArray(value)) {
                const parsed = parseInt(value[0]);
                return isNaN(parsed) ? null : parsed;
              }
              if (typeof value === 'string') {
                const parsed = parseInt(value);
                return isNaN(parsed) ? null : parsed;
              }
              if (typeof value === 'number') {
                return isNaN(value) ? null : value;
              }
              return null;
            };
            
            const formatted = {};
            if (ownership.id && ownership.id > 0) formatted.id = ownership.id;
            formatted.propertyId = propertyId;
            formatted.ownershipNameAr = ownership.ownershipNameAr || '';
            formatted.ownershipNameEng = ownership.ownershipNameEng || '';
            if (ownership.dob) formatted.dob = ownership.dob;
            if (parseId(ownership.nationalityId) !== null) formatted.nationalityId = parseId(ownership.nationalityId);
            if (parseId(ownership.identityTypeId) !== null) formatted.identityTypeId = parseId(ownership.identityTypeId);
            if (ownership.identityNumber) formatted.identityNumber = ownership.identityNumber;
            if (ownership.mobileNumberCountryCode) formatted.mobileNumberCountryCode = ownership.mobileNumberCountryCode;
            if (ownership.mobileNumber) formatted.mobileNumber = ownership.mobileNumber;
            if (ownership.emailId) formatted.emailId = ownership.emailId;
            if (parseId(ownership.bankId) !== null) formatted.bankId = parseId(ownership.bankId);
            if (ownership.bankAccountNumber) {
              formatted.bankAccountNumber = String(ownership.bankAccountNumber).trim();
            }
            if (parseId(ownership.areaId) !== null) formatted.areaId = parseId(ownership.areaId);
            if (parseId(ownership.cityId) !== null) formatted.cityId = parseId(ownership.cityId);
            if (parseId(ownership.districtId) !== null) formatted.districtId = parseId(ownership.districtId);
            if (ownership.streetName) formatted.streetName = ownership.streetName;
            if (ownership.buildingNumber) formatted.buildingNumber = ownership.buildingNumber;
            if (ownership.additionalNumber) formatted.additionalNumber = ownership.additionalNumber;
            if (ownership.postalCode) formatted.postalCode = ownership.postalCode;
            formatted.isAgent = ownership.isAgent === true;
            if (ownership.agentNumber) formatted.agentNumber = ownership.agentNumber;
            if (ownerIdValue !== null) formatted.ownerId = ownerIdValue;
            
            return formatted;
          });
          
          const tab2SubmitData = { ownerships: formattedOwnerships };
          const response = await propertyAPI.saveTab2(propertyId, tab2SubmitData);
          
          // Reload ownerships from backend to get updated IDs and prevent duplicates
          if (response?.data?.property?.propertyOwnerships || response?.data?.property?.PropertyOwnerships) {
            const savedOwnerships = response.data.property.propertyOwnerships || response.data.property.PropertyOwnerships || [];
            const formattedOwnerships = savedOwnerships.map(po => ({
              id: po.id || po.Id || null,
              ownershipNameAr: po.ownershipNameAr || po.OwnershipNameAr || '',
              ownershipNameEng: po.ownershipNameEng || po.OwnershipNameEng || '',
              dob: po.dob || po.DOB || '',
              nationalityId: po.nationalityId || po.NationalityId || null,
              identityTypeId: po.identityTypeId || po.IdentityTypeId || null,
              identityNumber: po.identityNumber || po.IdentityNumber || '',
              mobileNumberCountryCode: po.mobileNumberCountryCode || po.MobileNumberCountryCode || '',
              mobileNumber: po.mobileNumber || po.MobileNumber || '',
              emailId: po.emailId || po.EmailId || '',
              bankId: po.bankId || po.BankId || null,
              bankAccountNumber: po.bankAccountNumber || po.BankAccountNumber || '',
              areaId: po.areaId || po.AreaId || null,
              cityId: po.cityId || po.CityId || null,
              districtId: po.districtId || po.DistrictId || null,
              streetName: po.streetName || po.StreetName || '',
              buildingNumber: po.buildingNumber || po.BuildingNumber || '',
              additionalNumber: po.additionalNumber || po.AdditionalNumber || '',
              postalCode: po.postalCode || po.PostalCode || '',
              isAgent: po.isAgent || po.IsAgent || false,
              agentNumber: po.agentNumber || po.AgentNumber || '',
              ownerId: po.ownerId || po.OwnerId || null
            }));
            setOwnerships(formattedOwnerships);
          } else {
            // Fallback: reload property data to get updated ownerships
            try {
              const propertyResponse = await propertyAPI.getById(propertyId);
              const property = propertyResponse.data;
              if (property?.propertyOwnerships || property?.PropertyOwnerships) {
                const savedOwnerships = property.propertyOwnerships || property.PropertyOwnerships || [];
                const formattedOwnerships = savedOwnerships.map(po => ({
                  id: po.id || po.Id || null,
                  ownershipNameAr: po.ownershipNameAr || po.OwnershipNameAr || '',
                  ownershipNameEng: po.ownershipNameEng || po.OwnershipNameEng || '',
                  dob: po.dob || po.DOB || '',
                  nationalityId: po.nationalityId || po.NationalityId || null,
                  identityTypeId: po.identityTypeId || po.IdentityTypeId || null,
                  identityNumber: po.identityNumber || po.IdentityNumber || '',
                  mobileNumberCountryCode: po.mobileNumberCountryCode || po.MobileNumberCountryCode || '',
                  mobileNumber: po.mobileNumber || po.MobileNumber || '',
                  emailId: po.emailId || po.EmailId || '',
                  bankId: po.bankId || po.BankId || null,
                  bankAccountNumber: po.bankAccountNumber || po.BankAccountNumber || '',
                  areaId: po.areaId || po.AreaId || null,
                  cityId: po.cityId || po.CityId || null,
                  districtId: po.districtId || po.DistrictId || null,
                  streetName: po.streetName || po.StreetName || '',
                  buildingNumber: po.buildingNumber || po.BuildingNumber || '',
                  additionalNumber: po.additionalNumber || po.AdditionalNumber || '',
                  postalCode: po.postalCode || po.PostalCode || '',
                  isAgent: po.isAgent || po.IsAgent || false,
                  agentNumber: po.agentNumber || po.AgentNumber || '',
                  ownerId: po.ownerId || po.OwnerId || null
                }));
                setOwnerships(formattedOwnerships);
              }
            } catch (reloadErr) {
              console.error('Error reloading property data:', reloadErr);
              // Continue even if reload fails
            }
          }
        } catch (err) {
          setErrorMessage(err.response?.data?.message || t('property.create.messages.saveTab2Error'));
          setShowErrorModal(true);
          setSaving(false);
          return;
        } finally {
          setSaving(false);
        }
      } else if (activeTab === 3) {
        if (!propertyId) {
          setErrorMessage(t('property.create.messages.completePreviousTabs'));
          setShowErrorModal(true);
          return;
        }
        // Validate Tab 3 before saving
        setTab3FormSubmitted(true);
        const tab3Errors = validateTab3();
        if (tab3Errors.length > 0) {
          return;
        }
        try {
          setSaving(true);
          // Format facilities data for API (same logic as handleNext)
          const selectedFacilities = Object.keys(tab3Data.selectedFacilities);
          const facilityQuantities = {};
          selectedFacilities.forEach(facilityId => {
            facilityQuantities[parseInt(facilityId)] = tab3Data.selectedFacilities[facilityId];
          });

          const submitData = {
            municipalLicenseNumber: tab3Data.municipalLicenseNumber,
            realEstateTypeId: tab3Data.realEstateTypeId ? parseInt(tab3Data.realEstateTypeId) : null,
            realEstateStatus: tab3Data.realEstateStatus ? parseInt(tab3Data.realEstateStatus) : null,
            realEstateUsageId: tab3Data.realEstateUsageId ? parseInt(tab3Data.realEstateUsageId) : null,
            realEstateNumber: tab3Data.realEstateNumber,
            unitFloor: tab3Data.unitFloor ? parseInt(tab3Data.unitFloor) : null,
            realEstateAge: tab3Data.realEstateAge ? parseInt(tab3Data.realEstateAge) : null,
            unitTotalNumber: tab3Data.unitTotalNumber ? parseInt(tab3Data.unitTotalNumber) : null,
            floorsTotalNumber: tab3Data.floorsTotalNumber ? parseInt(tab3Data.floorsTotalNumber) : null,
            areaId: tab3Data.areaId ? parseInt(tab3Data.areaId) : null,
            cityId: tab3Data.cityId ? parseInt(tab3Data.cityId) : null,
            districtId: tab3Data.districtId ? parseInt(tab3Data.districtId) : null,
            streetName: tab3Data.streetName,
            buildingNumber: tab3Data.buildingNumber,
            additionalNumber: tab3Data.additionalNumber,
            postalCode: tab3Data.postalCode,
            isPartOfCompound: tab3Data.isPartOfCompound,
            selectedFacilitiesAndAmenities: selectedFacilities.map(id => parseInt(id)),
            facilityQuantities: facilityQuantities
          };
          await propertyAPI.saveTab3(propertyId, submitData);
        } catch (err) {
          setErrorMessage(t('property.create.messages.saveTab3Error'));
          setShowErrorModal(true);
          setSaving(false);
          return;
        } finally {
          setSaving(false);
        }
      } else if (activeTab === 4) {
        if (!propertyId) {
          setErrorMessage(t('property.create.messages.completePreviousTabs'));
          setShowErrorModal(true);
          return;
        }
        try {
          setSaving(true);
          // Format units data (same logic as handleNext)
          const formattedUnits = units.map(unit => ({
            id: unit.id || 0,
            propertyId: parseInt(propertyId),
            unitNumber: unit.unitNumber || null,
            unitTypeId: unit.unitTypeId ? parseInt(unit.unitTypeId) : null,
            unitUsageId: unit.unitUsageId ? parseInt(unit.unitUsageId) : null,
            floorNumber: unit.floorNumber ? parseInt(unit.floorNumber) : null,
            unitArea: unit.unitArea ? parseFloat(unit.unitArea) : null,
            numberOfRooms: unit.numberOfRooms ? parseInt(unit.numberOfRooms) : null,
            establishedDate: unit.establishedDate || null,
            unitDirection: unit.unitDirection || null,
            unitFinishing: unit.unitFinishing || null,
            unitFrontLength: unit.unitFrontLength ? parseFloat(unit.unitFrontLength) : null,
            unitLength: unit.unitLength ? parseFloat(unit.unitLength) : null,
            unitWidth: unit.unitWidth ? parseFloat(unit.unitWidth) : null,
            unitHeight: unit.unitHeight ? parseFloat(unit.unitHeight) : null,
            includeMezzanine: unit.includeMezzanine || false,
            furnished: unit.furnished || false,
            kitchenCabinets: unit.kitchenCabinets || false,
            electricityMeter: unit.electricityMeter || null,
            gasMeterNo: unit.gasMeterNo || null,
            waterMeter: unit.waterMeter || null,
            countOfParking: unit.countOfParking ? parseInt(unit.countOfParking) : null,
            selectedFacilities: unit.selectedFacilities || {}
          }));
          const tab4SubmitData = { 
            units: formattedUnits,
            deletedUnitIds: deletedUnitIds
          };
          await propertyAPI.saveTab4(propertyId, tab4SubmitData);
          setDeletedUnitIds([]);
        } catch (err) {
          setErrorMessage(t('property.create.messages.saveTab4Error'));
          setShowErrorModal(true);
          setSaving(false);
          return;
        } finally {
          setSaving(false);
        }
      }
    }
    
    setActiveTab(tabNumber);
  };

  const handleTab1Change = (e) => {
    const { name, value, type, checked } = e.target;
    setTab1Data(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      const newErrors = { ...errors, [name]: '' };
      // Clear conditional field errors when ownerDocumentType changes
      if (name === 'ownerDocumentType') {
        if (value === '1') {
          delete newErrors.crNumber;
        } else if (value === '2') {
          delete newErrors.identityNumber;
        }
      }
      setErrors(newErrors);
    }
  };

  const saveTab1 = async () => {
    if (!validateTab1()) return false;

    setSaving(true);
    try {
      const submitData = {
        ...tab1Data,
        ownerDocumentType: parseInt(tab1Data.ownerDocumentType),
        placeTypeCityId: tab1Data.placeTypeCityId ? parseInt(tab1Data.placeTypeCityId) : null,
        branchId: tab1Data.branchId ? parseInt(tab1Data.branchId) : null,
        issueDate: tab1Data.issueDate || null
      };

      // Log Tab 1 submitted data
      console.log('=== TAB 1 (Property Information) - Submitted Data ===');
      console.log('Property ID:', propertyId || 'NEW');
      console.log('Operation:', propertyId ? 'UPDATE' : 'CREATE');
      console.log('Submit Data:', JSON.stringify(submitData, null, 2));
      console.log('==================================================');

      let response;
      let extractedPropertyId = propertyId;

      if (propertyId) {
        // Property already exists - use UPDATE
        // Include id in the body as backend expects id to match URL parameter
        const updateData = { ...submitData, id: parseInt(propertyId) };
        console.log('Property already exists, calling UPDATE API');
        response = await propertyAPI.update(propertyId, updateData);
        // Extract property ID from update response to ensure it's still correct
        const updatePropertyId = response.data?.id || response.data?.Id || 
                                 response.data?.property?.id || response.data?.Property?.Id || 
                                 propertyId;
        extractedPropertyId = updatePropertyId;
        console.log('Update response - Property ID:', extractedPropertyId);
      } else {
        // Property doesn't exist yet - use CREATE
        console.log('Property does not exist, calling CREATE API');
        response = await propertyAPI.create(submitData);
        // Extract property ID from response - try multiple possible response formats
        const createPropertyId = response.data?.id || response.data?.Id || 
                                response.data?.property?.id || response.data?.Property?.Id ||
                                response.data?.propertyId || response.data?.PropertyId;
        
        if (createPropertyId) {
          extractedPropertyId = typeof createPropertyId === 'number' ? createPropertyId : parseInt(createPropertyId);
          console.log('Create response - Property ID:', extractedPropertyId);
        } else {
          console.error('Failed to extract property ID from create response:', {
            response: response.data,
            fullResponse: response
          });
          setErrorMessage(t('property.create.messages.saveTab1Error'));
          setShowErrorModal(true);
          return false;
        }
      }

      // Always set the propertyId after successful save (create or update)
      // This ensures that subsequent saves will use UPDATE
      if (extractedPropertyId && extractedPropertyId !== propertyId) {
        setPropertyId(extractedPropertyId);
        console.log('Property ID set to:', extractedPropertyId);
      }

      return true;
    } catch (err) {
      console.error('Error saving Tab 1:', err);
      console.error('Error response:', err.response?.data);
      setErrorMessage(err.response?.data?.message || t('property.create.messages.saveTab1Error'));
      setShowErrorModal(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (activeTab === 1) {
      const saved = await saveTab1();
      if (!saved) return;
    } else if (activeTab === 2) {
      if (!propertyId) {
        setErrorMessage(t('property.create.messages.completeTab1First'));
        setShowErrorModal(true);
        return;
      }
      // Validate that at least one owner or agent is added
      if (ownerships.length === 0) {
        setErrorMessage(t('property.create.errors.ownershipRequired', 'Please add at least one owner or agent'));
        setShowErrorModal(true);
        return;
      }
      // Save ownerships via Tab2 endpoint
      let tab2SubmitData = null;
      try {
        setSaving(true);
        // Format ownerships data to match API model exactly
        const formattedOwnerships = ownerships.map(ownership => {
          // Ensure ownerId is properly formatted - it should be null or an integer, never an array or empty string
          let ownerIdValue = null;
          if (ownership.ownerId) {
            if (Array.isArray(ownership.ownerId)) {
              // If it's an array, take the first element and convert to int
              const firstVal = ownership.ownerId[0];
              if (firstVal && firstVal.toString().trim() !== '') {
                const parsed = parseInt(firstVal.toString());
                ownerIdValue = isNaN(parsed) ? null : parsed;
              }
            } else if (typeof ownership.ownerId === 'string') {
              const trimmed = ownership.ownerId.trim();
              if (trimmed !== '') {
                const parsed = parseInt(trimmed);
                ownerIdValue = isNaN(parsed) ? null : parsed;
              }
            } else if (typeof ownership.ownerId === 'number') {
              ownerIdValue = isNaN(ownership.ownerId) ? null : ownership.ownerId;
            }
          }
          
          // Helper function to safely parse IDs
          const parseId = (value) => {
            if (!value) return null;
            if (Array.isArray(value)) {
              const parsed = parseInt(value[0]);
              return isNaN(parsed) ? null : parsed;
            }
            if (typeof value === 'string') {
              const parsed = parseInt(value);
              return isNaN(parsed) ? null : parsed;
            }
            if (typeof value === 'number') {
              return isNaN(value) ? null : value;
            }
            return null;
          };
          
          // Build ownership object with ONLY the fields expected by the API
          // Do not include any navigation properties (Property, Owner, Nationality, etc.)
          // These are server-side only and should never be sent from the client
          const formatted = {};
          
          // Only add fields that are part of the PropertyOwnership model (not navigation properties)
          // Explicitly set each field to avoid including any unexpected properties
          if (ownership.id && ownership.id > 0) formatted.id = ownership.id;
          formatted.propertyId = propertyId;
          formatted.ownershipNameAr = ownership.ownershipNameAr || '';
          formatted.ownershipNameEng = ownership.ownershipNameEng || '';
          // Backend expects DOB (uppercase), not dob
          if (ownership.dob) formatted.dob = ownership.dob;
          if (parseId(ownership.nationalityId) !== null) formatted.nationalityId = parseId(ownership.nationalityId);
          if (parseId(ownership.identityTypeId) !== null) formatted.identityTypeId = parseId(ownership.identityTypeId);
          if (ownership.identityNumber) formatted.identityNumber = ownership.identityNumber;
          if (ownership.mobileNumberCountryCode) formatted.mobileNumberCountryCode = ownership.mobileNumberCountryCode;
          if (ownership.mobileNumber) formatted.mobileNumber = ownership.mobileNumber;
          if (ownership.emailId) formatted.emailId = ownership.emailId;
          if (parseId(ownership.bankId) !== null) formatted.bankId = parseId(ownership.bankId);
          // Ensure bankAccountNumber is always a string, never converted to number
          if (ownership.bankAccountNumber) {
            formatted.bankAccountNumber = String(ownership.bankAccountNumber).trim();
          }
          if (parseId(ownership.areaId) !== null) formatted.areaId = parseId(ownership.areaId);
          if (parseId(ownership.cityId) !== null) formatted.cityId = parseId(ownership.cityId);
          if (parseId(ownership.districtId) !== null) formatted.districtId = parseId(ownership.districtId);
          if (ownership.streetName) formatted.streetName = ownership.streetName;
          if (ownership.buildingNumber) formatted.buildingNumber = ownership.buildingNumber;
          if (ownership.additionalNumber) formatted.additionalNumber = ownership.additionalNumber;
          if (ownership.postalCode) formatted.postalCode = ownership.postalCode;
          formatted.isAgent = ownership.isAgent === true;
          if (ownership.agentNumber) formatted.agentNumber = ownership.agentNumber;
          if (ownerIdValue !== null) formatted.ownerId = ownerIdValue;
          
          // Explicitly exclude navigation properties to prevent model binding errors
          // These should NEVER be included in the payload
          delete formatted.Property;
          delete formatted.Nationality;
          delete formatted.IdentityType;
          delete formatted.Bank;
          delete formatted.Area;
          delete formatted.City;
          delete formatted.District;
          delete formatted.Owner;
          
          return formatted;
        });
        
        // Clean the data to ensure no navigation properties are included
        const cleanedOwnerships = formattedOwnerships.map(ownership => {
          // Create a new object with only the allowed fields
          const clean = {
            ...(ownership.id && ownership.id > 0 ? { id: ownership.id } : {}),
            propertyId: ownership.propertyId,
            ownershipNameAr: ownership.ownershipNameAr,
            ownershipNameEng: ownership.ownershipNameEng,
            ...(ownership.dob ? { dob: ownership.dob } : {}),
            ...(ownership.nationalityId !== null && ownership.nationalityId !== undefined ? { nationalityId: ownership.nationalityId } : {}),
            ...(ownership.identityTypeId !== null && ownership.identityTypeId !== undefined ? { identityTypeId: ownership.identityTypeId } : {}),
            ...(ownership.identityNumber ? { identityNumber: ownership.identityNumber } : {}),
            ...(ownership.mobileNumberCountryCode ? { mobileNumberCountryCode: ownership.mobileNumberCountryCode } : {}),
            ...(ownership.mobileNumber ? { mobileNumber: ownership.mobileNumber } : {}),
            ...(ownership.emailId ? { emailId: ownership.emailId } : {}),
            ...(ownership.bankId !== null && ownership.bankId !== undefined ? { bankId: ownership.bankId } : {}),
            ...(ownership.bankAccountNumber ? { bankAccountNumber: ownership.bankAccountNumber } : {}),
            ...(ownership.areaId !== null && ownership.areaId !== undefined ? { areaId: ownership.areaId } : {}),
            ...(ownership.cityId !== null && ownership.cityId !== undefined ? { cityId: ownership.cityId } : {}),
            ...(ownership.districtId !== null && ownership.districtId !== undefined ? { districtId: ownership.districtId } : {}),
            ...(ownership.streetName ? { streetName: ownership.streetName } : {}),
            ...(ownership.buildingNumber ? { buildingNumber: ownership.buildingNumber } : {}),
            ...(ownership.additionalNumber ? { additionalNumber: ownership.additionalNumber } : {}),
            ...(ownership.postalCode ? { postalCode: ownership.postalCode } : {}),
            isAgent: ownership.isAgent,
            ...(ownership.agentNumber ? { agentNumber: ownership.agentNumber } : {}),
            ...(ownership.ownerId !== null && ownership.ownerId !== undefined ? { ownerId: ownership.ownerId } : {})
          };
          return clean;
        });
        
        const tab2SubmitData = { ownerships: cleanedOwnerships };
        
        // Log Tab 2 submitted data
        console.log('=== TAB 2 (Ownership Information) - Submitted Data ===');
        console.log('Property ID:', propertyId);
        console.log('Submit Data:', JSON.stringify(tab2SubmitData, null, 2));
        console.log('Number of Ownerships:', cleanedOwnerships.length);
        console.log('==================================================');
        
        const response = await propertyAPI.saveTab2(propertyId, tab2SubmitData);
        
        // Reload ownerships from backend to get updated IDs and prevent duplicates
        if (response?.data?.property?.propertyOwnerships || response?.data?.property?.PropertyOwnerships) {
          const savedOwnerships = response.data.property.propertyOwnerships || response.data.property.PropertyOwnerships || [];
          const formattedOwnerships = savedOwnerships.map(po => ({
            id: po.id || po.Id || null,
            ownershipNameAr: po.ownershipNameAr || po.OwnershipNameAr || '',
            ownershipNameEng: po.ownershipNameEng || po.OwnershipNameEng || '',
            dob: po.dob || po.DOB || '',
            nationalityId: po.nationalityId || po.NationalityId || null,
            identityTypeId: po.identityTypeId || po.IdentityTypeId || null,
            identityNumber: po.identityNumber || po.IdentityNumber || '',
            mobileNumberCountryCode: po.mobileNumberCountryCode || po.MobileNumberCountryCode || '',
            mobileNumber: po.mobileNumber || po.MobileNumber || '',
            emailId: po.emailId || po.EmailId || '',
            bankId: po.bankId || po.BankId || null,
            bankAccountNumber: po.bankAccountNumber || po.BankAccountNumber || '',
            areaId: po.areaId || po.AreaId || null,
            cityId: po.cityId || po.CityId || null,
            districtId: po.districtId || po.DistrictId || null,
            streetName: po.streetName || po.StreetName || '',
            buildingNumber: po.buildingNumber || po.BuildingNumber || '',
            additionalNumber: po.additionalNumber || po.AdditionalNumber || '',
            postalCode: po.postalCode || po.PostalCode || '',
            isAgent: po.isAgent || po.IsAgent || false,
            agentNumber: po.agentNumber || po.AgentNumber || '',
            ownerId: po.ownerId || po.OwnerId || null
          }));
          setOwnerships(formattedOwnerships);
        } else {
          // Fallback: reload property data to get updated ownerships
          try {
            const propertyResponse = await propertyAPI.getById(propertyId);
            const property = propertyResponse.data;
            if (property?.propertyOwnerships || property?.PropertyOwnerships) {
              const savedOwnerships = property.propertyOwnerships || property.PropertyOwnerships || [];
              const formattedOwnerships = savedOwnerships.map(po => ({
                id: po.id || po.Id || null,
                ownershipNameAr: po.ownershipNameAr || po.OwnershipNameAr || '',
                ownershipNameEng: po.ownershipNameEng || po.OwnershipNameEng || '',
                dob: po.dob || po.DOB || '',
                nationalityId: po.nationalityId || po.NationalityId || null,
                identityTypeId: po.identityTypeId || po.IdentityTypeId || null,
                identityNumber: po.identityNumber || po.IdentityNumber || '',
                mobileNumberCountryCode: po.mobileNumberCountryCode || po.MobileNumberCountryCode || '',
                mobileNumber: po.mobileNumber || po.MobileNumber || '',
                emailId: po.emailId || po.EmailId || '',
                bankId: po.bankId || po.BankId || null,
                bankAccountNumber: po.bankAccountNumber || po.BankAccountNumber || '',
                areaId: po.areaId || po.AreaId || null,
                cityId: po.cityId || po.CityId || null,
                districtId: po.districtId || po.DistrictId || null,
                streetName: po.streetName || po.StreetName || '',
                buildingNumber: po.buildingNumber || po.BuildingNumber || '',
                additionalNumber: po.additionalNumber || po.AdditionalNumber || '',
                postalCode: po.postalCode || po.PostalCode || '',
                isAgent: po.isAgent || po.IsAgent || false,
                agentNumber: po.agentNumber || po.AgentNumber || '',
                ownerId: po.ownerId || po.OwnerId || null
              }));
              setOwnerships(formattedOwnerships);
            }
          } catch (reloadErr) {
            console.error('Error reloading property data:', reloadErr);
            // Continue even if reload fails
          }
        }
      } catch (err) {
        // Log error details for debugging
        console.error('=== TAB 2 Save Error ===');
        console.error('Error:', err);
        console.error('Error Response:', err.response?.data);
        console.error('Error Status:', err.response?.status);
        if (tab2SubmitData) {
          console.error('Submitted Data:', JSON.stringify(tab2SubmitData, null, 2));
        }
        console.error('========================');
        
        const errorMessage = err.response?.data?.message || err.message || t('property.create.messages.saveTab2Error');
        setErrorMessage(errorMessage);
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (activeTab === 3) {
      if (!propertyId) {
        setErrorMessage(t('property.create.messages.completePreviousTabs'));
        setShowErrorModal(true);
        return;
      }
      // Validate Tab 3 before saving
      setTab3FormSubmitted(true);
      const tab3Errors = validateTab3();
      if (tab3Errors.length > 0) {
        return;
      }
      try {
        setSaving(true);
        // Format facilities data for API
        const selectedFacilities = Object.keys(tab3Data.selectedFacilities);
        const facilityQuantities = {};
        selectedFacilities.forEach(facilityId => {
          facilityQuantities[parseInt(facilityId)] = tab3Data.selectedFacilities[facilityId];
        });

        const submitData = {
          municipalLicenseNumber: tab3Data.municipalLicenseNumber,
          realEstateTypeId: tab3Data.realEstateTypeId ? parseInt(tab3Data.realEstateTypeId) : null,
          realEstateStatus: tab3Data.realEstateStatus ? parseInt(tab3Data.realEstateStatus) : null,
          realEstateUsageId: tab3Data.realEstateUsageId ? parseInt(tab3Data.realEstateUsageId) : null,
          realEstateNumber: tab3Data.realEstateNumber,
          unitFloor: tab3Data.unitFloor ? parseInt(tab3Data.unitFloor) : null,
          realEstateAge: tab3Data.realEstateAge ? parseInt(tab3Data.realEstateAge) : null,
          unitTotalNumber: tab3Data.unitTotalNumber ? parseInt(tab3Data.unitTotalNumber) : null,
          floorsTotalNumber: tab3Data.floorsTotalNumber ? parseInt(tab3Data.floorsTotalNumber) : null,
          areaId: tab3Data.areaId ? parseInt(tab3Data.areaId) : null,
          cityId: tab3Data.cityId ? parseInt(tab3Data.cityId) : null,
          districtId: tab3Data.districtId ? parseInt(tab3Data.districtId) : null,
          streetName: tab3Data.streetName,
          buildingNumber: tab3Data.buildingNumber,
          additionalNumber: tab3Data.additionalNumber,
          postalCode: tab3Data.postalCode,
          isPartOfCompound: tab3Data.isPartOfCompound,
          selectedFacilitiesAndAmenities: selectedFacilities.map(id => parseInt(id)),
          facilityQuantities: facilityQuantities
        };
        
        // Log Tab 3 submitted data
        console.log('=== TAB 3 (Real Estate Details) - Submitted Data ===');
        console.log('Property ID:', propertyId);
        console.log('Submit Data:', JSON.stringify(submitData, null, 2));
        console.log('Selected Facilities Count:', selectedFacilities.length);
        console.log('==================================================');
        
        await propertyAPI.saveTab3(propertyId, submitData);
      } catch (err) {
        setErrorMessage(t('property.create.messages.saveTab3Error'));
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (activeTab === 4) {
      if (!propertyId) {
        setErrorMessage(t('property.create.messages.completePreviousTabs'));
        setShowErrorModal(true);
        return;
      }
      try {
        setSaving(true);
        // Format units data - convert empty strings to null for numeric fields, keep strings as strings
        const formattedUnits = units.map(unit => ({
          id: unit.id || 0,
          propertyId: parseInt(propertyId), // Include propertyId for foreign key constraint
          unitNumber: unit.unitNumber || null,
          unitTypeId: unit.unitTypeId ? parseInt(unit.unitTypeId) : null,
          unitUsageId: unit.unitUsageId ? parseInt(unit.unitUsageId) : null,
          floorNumber: unit.floorNumber ? parseInt(unit.floorNumber) : null,
          unitArea: unit.unitArea ? parseFloat(unit.unitArea) : null,
          numberOfRooms: unit.numberOfRooms ? parseInt(unit.numberOfRooms) : null,
          establishedDate: unit.establishedDate || null,
          unitDirection: unit.unitDirection || null,
          unitFinishing: unit.unitFinishing || null,
          unitFrontLength: unit.unitFrontLength ? parseFloat(unit.unitFrontLength) : null,
          unitLength: unit.unitLength ? parseFloat(unit.unitLength) : null,
          unitWidth: unit.unitWidth ? parseFloat(unit.unitWidth) : null,
          unitHeight: unit.unitHeight ? parseFloat(unit.unitHeight) : null,
          includeMezzanine: unit.includeMezzanine || false,
          furnished: unit.furnished || false,
          kitchenCabinets: unit.kitchenCabinets || false,
          electricityMeter: unit.electricityMeter || null,
          gasMeterNo: unit.gasMeterNo || null,
          waterMeter: unit.waterMeter || null,
          countOfParking: unit.countOfParking ? parseInt(unit.countOfParking) : null,
          selectedFacilities: unit.selectedFacilities || {}
        }));
        const tab4SubmitData = { 
          units: formattedUnits,
          deletedUnitIds: deletedUnitIds // Send deleted unit IDs to backend
        };
        
        // Log Tab 4 submitted data
        console.log('=== TAB 4 (Units) - Submitted Data ===');
        console.log('Property ID:', propertyId);
        console.log('Submit Data:', JSON.stringify(tab4SubmitData, null, 2));
        console.log('Number of Units:', formattedUnits.length);
        console.log('Deleted Unit IDs:', deletedUnitIds);
        console.log('==================================================');
        
        await propertyAPI.saveTab4(propertyId, tab4SubmitData);
        // Clear deleted IDs after successful save
        setDeletedUnitIds([]);
      } catch (err) {
        setErrorMessage(t('property.create.messages.saveTab4Error'));
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    if (activeTab < 5) {
      setActiveTab(activeTab + 1);
    }
  };

  const handlePrevious = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleFinish = async () => {
    // Validate at least one attachment is required
    if (attachments.length === 0) {
      setErrorMessage(t('property.create.errors.attachmentRequired', 'At least one attachment is required'));
      setShowErrorModal(true);
      return;
    }
    
    // Save attachments
    if (propertyId && attachments.length > 0) {
      try {
        setSaving(true);
        const formData = new FormData();
        // Add new files and their names
        const attachmentInfo = [];
        attachments.forEach(attachment => {
          if (attachment.file) {
            formData.append('AttachmentFiles', attachment.file);
            formData.append('AttachmentNames', attachment.name || attachment.file.name);
            attachmentInfo.push({
              name: attachment.name || attachment.file.name,
              fileName: attachment.file.name,
              fileSize: attachment.file.size,
              fileType: attachment.file.type
            });
          } else if (attachment.filePath) {
            attachmentInfo.push({
              name: attachment.name,
              filePath: attachment.filePath
            });
          }
        });
        
        // Log Tab 5 submitted data
        console.log('=== TAB 5 (Attachments) - Submitted Data ===');
        console.log('Property ID:', propertyId);
        console.log('Number of Attachments:', attachments.length);
        console.log('Attachment Info:', JSON.stringify(attachmentInfo, null, 2));
        console.log('FormData Keys:', Array.from(formData.keys()));
        console.log('==================================================');
        
        await propertyAPI.saveTab5(propertyId, formData);
      } catch (err) {
        setErrorMessage(t('property.create.messages.saveAttachmentsError'));
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    setShowSuccessModal(true);
    setTimeout(() => {
      navigate('/property');
    }, 1500);
  };

  // Ownership/Agent functions
  const handleAddOwner = () => {
    setOwnershipFormData({
      id: null, // New ownership has no ID
      ownershipNameAr: '',
      ownershipNameEng: '',
      dob: '',
      nationalityId: '',
      identityTypeId: '',
      identityNumber: '',
      mobileNumberCountryCode: '',
      mobileNumber: '',
      emailId: '',
      bankId: '',
      bankAccountNumber: '',
      areaId: '',
      cityId: '',
      districtId: '',
      streetName: '',
      buildingNumber: '',
      additionalNumber: '',
      postalCode: '',
      isAgent: false,
      agentNumber: '',
      ownerId: ''
    });
    setEditingOwnershipIndex(null);
    setOwnershipFormSubmitted(false);
    setShowOwnershipModal(true);
  };

  const handleAddAgent = () => {
    setOwnershipFormData({
      id: null, // New agent has no ID
      ownershipNameAr: '',
      ownershipNameEng: '',
      dob: '',
      nationalityId: '',
      identityTypeId: '',
      identityNumber: '',
      mobileNumberCountryCode: '',
      mobileNumber: '',
      emailId: '',
      bankId: '',
      bankAccountNumber: '',
      areaId: '',
      cityId: '',
      districtId: '',
      streetName: '',
      buildingNumber: '',
      additionalNumber: '',
      postalCode: '',
      isAgent: true,
      agentNumber: '',
      ownerId: ''
    });
    setEditingOwnershipIndex(null);
    setOwnershipFormSubmitted(false);
    setShowAgentModal(true);
  };

  const handleOwnershipFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOwnershipFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Validate ownership/agent form
  const validateOwnershipForm = () => {
    const errors = [];
    
    // Required fields for both owner and agent
    if (!ownershipFormData.ownershipNameAr.trim()) {
      errors.push(t('property.create.errors.ownershipNameArRequired', 'Ownership Name (Arabic) is required'));
    }
    if (!ownershipFormData.ownershipNameEng.trim()) {
      errors.push(t('property.create.errors.ownershipNameEngRequired', 'Ownership Name (English) is required'));
    }
    if (!ownershipFormData.dob) {
      errors.push(t('property.create.errors.dobRequired', 'Date of Birth is required'));
    }
    if (!ownershipFormData.nationalityId) {
      errors.push(t('property.create.errors.nationalityRequired', 'Nationality is required'));
    }
    if (!ownershipFormData.identityTypeId) {
      errors.push(t('property.create.errors.identityTypeRequired', 'Identity Type is required'));
    }
    if (!ownershipFormData.identityNumber.trim()) {
      errors.push(t('property.create.errors.identityNumberRequired', 'Identity Number is required'));
    }
    if (!ownershipFormData.mobileNumberCountryCode) {
      errors.push(t('property.create.errors.mobileCountryCodeRequired', 'Mobile Country Code is required'));
    }
    if (!ownershipFormData.mobileNumber.trim()) {
      errors.push(t('property.create.errors.mobileNumberRequired', 'Mobile Number is required'));
    }
    if (!ownershipFormData.emailId.trim()) {
      errors.push(t('property.create.errors.emailRequired', 'Email is required'));
    }
    if (!ownershipFormData.bankId) {
      errors.push(t('property.create.errors.bankRequired', 'Bank is required'));
    }
    if (!ownershipFormData.bankAccountNumber.trim()) {
      errors.push(t('property.create.errors.bankAccountRequired', 'Bank Account Number is required'));
    }
    if (!ownershipFormData.areaId) {
      errors.push(t('property.create.errors.areaRequired', 'Area is required'));
    }
    if (!ownershipFormData.cityId) {
      errors.push(t('property.create.errors.cityRequired', 'City is required'));
    }
    if (!ownershipFormData.districtId) {
      errors.push(t('property.create.errors.districtRequired', 'District is required'));
    }
    if (!ownershipFormData.streetName.trim()) {
      errors.push(t('property.create.errors.streetNameRequired', 'Street Name is required'));
    }
    if (!ownershipFormData.buildingNumber.trim()) {
      errors.push(t('property.create.errors.buildingNumberRequired', 'Building Number is required'));
    }
    if (!ownershipFormData.additionalNumber.trim()) {
      errors.push(t('property.create.errors.additionalNumberRequired', 'Additional Number is required'));
    }
    // postalCode is optional - no validation needed
    
    // Agent-specific validation
    if (ownershipFormData.isAgent && !ownershipFormData.agentNumber.trim()) {
      errors.push(t('property.create.errors.agentNumberRequired', 'Agent Number is required'));
    }
    
    return errors;
  };

  const handleSaveOwnership = () => {
    setOwnershipFormSubmitted(true);
    const validationErrors = validateOwnershipForm();
    if (validationErrors.length > 0) {
      return;
    }

    const newOwnership = {
      ...ownershipFormData,
      id: ownershipFormData.id || null, // Preserve original ID when editing
      nationalityId: ownershipFormData.nationalityId ? parseInt(ownershipFormData.nationalityId) : null,
      identityTypeId: ownershipFormData.identityTypeId ? parseInt(ownershipFormData.identityTypeId) : null,
      bankId: ownershipFormData.bankId ? parseInt(ownershipFormData.bankId) : null,
      // Ensure bankAccountNumber is always a string, never converted to number
      bankAccountNumber: ownershipFormData.bankAccountNumber ? String(ownershipFormData.bankAccountNumber).trim() : '',
      areaId: ownershipFormData.areaId ? parseInt(ownershipFormData.areaId) : null,
      cityId: ownershipFormData.cityId ? parseInt(ownershipFormData.cityId) : null,
      districtId: ownershipFormData.districtId ? parseInt(ownershipFormData.districtId) : null,
      ownerId: ownershipFormData.ownerId ? parseInt(ownershipFormData.ownerId) : null,
      dob: ownershipFormData.dob || null
    };

    if (editingOwnershipIndex !== null) {
      // Update existing ownership - preserve the original ID
      const updated = [...ownerships];
      updated[editingOwnershipIndex] = newOwnership;
      setOwnerships(updated);
      setEditingOwnershipIndex(null); // Reset editing index
    } else {
      // Add new ownership
      setOwnerships([...ownerships, newOwnership]);
    }

    setShowOwnershipModal(false);
    setShowAgentModal(false);
  };

  const handleEditOwnership = (index) => {
    const ownership = ownerships[index];
    setOwnershipFormData({
      ...ownership,
      id: ownership.id || null, // Preserve original ownership ID
      nationalityId: ownership.nationalityId?.toString() || '',
      identityTypeId: ownership.identityTypeId?.toString() || '',
      bankId: ownership.bankId?.toString() || '',
      areaId: ownership.areaId?.toString() || '',
      cityId: ownership.cityId?.toString() || '',
      districtId: ownership.districtId?.toString() || '',
      ownerId: ownership.ownerId?.toString() || '',
      dob: ownership.dob || ''
    });
    setEditingOwnershipIndex(index);
    setOwnershipFormSubmitted(false);
    if (ownership.isAgent) {
      setShowAgentModal(true);
    } else {
      setShowOwnershipModal(true);
    }
  };

  const handleDeleteOwnership = (index) => {
    setOwnerships(ownerships.filter((_, i) => i !== index));
  };

  // Unit handlers
  const handleAddUnit = () => {
    // Ensure dropdowns are loaded
    if (dropdowns.unitFacilities.length === 0 && dropdowns.unitTypes.length === 0) {
      loadDropdowns();
    }
    setEditingUnitIndex(null);
    setUnitFormSubmitted(false);
    setUnitFormData({
      id: null, // New unit has no ID
      unitNumber: '',
      unitTypeId: '',
      unitUsageId: '',
      floorNumber: '',
      unitArea: '',
      numberOfRooms: '',
      establishedDate: '',
      unitDirection: '',
      unitFinishing: '',
      unitFrontLength: '',
      unitLength: '',
      unitWidth: '',
      unitHeight: '',
      includeMezzanine: false,
      furnished: false,
      kitchenCabinets: false,
      electricityMeter: '',
      gasMeterNo: '',
      waterMeter: '',
      countOfParking: '',
      selectedFacilities: {}
    });
    setShowUnitModal(true);
  };

  const handleEditUnit = (index) => {
    const unit = units[index];
    setEditingUnitIndex(index);
    setUnitFormSubmitted(false);
    
    // Normalize selectedFacilities keys to numbers for consistent comparison
    const normalizedFacilities = {};
    if (unit.selectedFacilities) {
      Object.keys(unit.selectedFacilities).forEach(key => {
        const facilityId = parseInt(key);
        if (!isNaN(facilityId)) {
          normalizedFacilities[facilityId] = unit.selectedFacilities[key];
        }
      });
    }
    
    setUnitFormData({
      id: unit.id || null, // Preserve original unit ID
      unitNumber: unit.unitNumber || '',
      unitTypeId: unit.unitTypeId ? String(unit.unitTypeId) : '',
      unitUsageId: unit.unitUsageId ? String(unit.unitUsageId) : '',
      floorNumber: unit.floorNumber || '',
      unitArea: unit.unitArea || '',
      numberOfRooms: unit.numberOfRooms || '',
      establishedDate: unit.establishedDate || '',
      unitDirection: unit.unitDirection || '',
      unitFinishing: unit.unitFinishing || '',
      unitFrontLength: unit.unitFrontLength || '',
      unitLength: unit.unitLength || '',
      unitWidth: unit.unitWidth || '',
      unitHeight: unit.unitHeight || '',
      includeMezzanine: unit.includeMezzanine || false,
      furnished: unit.furnished || false,
      kitchenCabinets: unit.kitchenCabinets || false,
      electricityMeter: unit.electricityMeter || '',
      gasMeterNo: unit.gasMeterNo || '',
      waterMeter: unit.waterMeter || '',
      countOfParking: unit.countOfParking || '',
      selectedFacilities: normalizedFacilities
    });
    setShowUnitModal(true);
  };

  // Validate unit form - all mandatory except: unitLength, unitFrontLength, unitHeight, gasMeterNo, countOfParking, selectedFacilities
  const validateUnitForm = () => {
    const errors = [];
    
    if (!unitFormData.unitNumber.trim()) {
      errors.push(t('property.create.errors.unitNumberRequired', 'Unit Number is required'));
    }
    if (!unitFormData.unitTypeId) {
      errors.push(t('property.create.errors.unitTypeRequired', 'Unit Type is required'));
    }
    if (!unitFormData.unitUsageId) {
      errors.push(t('property.create.errors.unitUsageRequired', 'Unit Usage is required'));
    }
    if (!unitFormData.floorNumber) {
      errors.push(t('property.create.errors.floorNumberRequired', 'Floor Number is required'));
    }
    if (!unitFormData.unitArea) {
      errors.push(t('property.create.errors.unitAreaRequired', 'Unit Area is required'));
    }
    if (!unitFormData.numberOfRooms) {
      errors.push(t('property.create.errors.numberOfRoomsRequired', 'Number of Rooms is required'));
    }
    if (!unitFormData.establishedDate) {
      errors.push(t('property.create.errors.establishedDateRequired', 'Established Date is required'));
    }
    if (!unitFormData.unitDirection) {
      errors.push(t('property.create.errors.unitDirectionRequired', 'Unit Direction is required'));
    }
    if (!unitFormData.unitFinishing) {
      errors.push(t('property.create.errors.unitFinishingRequired', 'Unit Finishing is required'));
    }
    if (!unitFormData.unitWidth) {
      errors.push(t('property.create.errors.unitWidthRequired', 'Unit Width is required'));
    }
    if (!unitFormData.electricityMeter.trim()) {
      errors.push(t('property.create.errors.electricityMeterRequired', 'Electricity Meter is required'));
    }
    if (!unitFormData.waterMeter.trim()) {
      errors.push(t('property.create.errors.waterMeterRequired', 'Water Meter is required'));
    }
    // Optional fields (no validation needed):
    // - unitLength
    // - unitFrontLength
    // - unitHeight
    // - gasMeterNo
    // - countOfParking
    // - selectedFacilities
    
    return errors;
  };

  const handleSaveUnit = () => {
    // Validate unit form before saving
    setUnitFormSubmitted(true);
    const validationErrors = validateUnitForm();
    if (validationErrors.length > 0) {
      return;
    }
    
    // Find unit type and usage names
    const unitType = (dropdowns.unitTypes || []).find(t => t.id === parseInt(unitFormData.unitTypeId));
    const unitUsage = (dropdowns.unitUsages || []).find(u => u.id === parseInt(unitFormData.unitUsageId));
    
    // Map direction enum values
    const directionMap = {
      '1': t('property.create.fields.modals.unit.directions.north'),
      '2': t('property.create.fields.modals.unit.directions.south'),
      '3': t('property.create.fields.modals.unit.directions.east'),
      '4': t('property.create.fields.modals.unit.directions.west'),
      '5': t('property.create.fields.modals.unit.directions.northEast'),
      '6': t('property.create.fields.modals.unit.directions.northWest'),
      '7': t('property.create.fields.modals.unit.directions.southEast'),
      '8': t('property.create.fields.modals.unit.directions.southWest')
    };
    const directionName = unitFormData.unitDirection ? directionMap[unitFormData.unitDirection] : '';

    // Map finishing enum values
    const finishingMap = {
      '1': t('property.create.fields.modals.unit.finishing.basic'),
      '2': t('property.create.fields.modals.unit.finishing.standard'),
      '3': t('property.create.fields.modals.unit.finishing.premium'),
      '4': t('property.create.fields.modals.unit.finishing.luxury'),
      '5': t('property.create.fields.modals.unit.finishing.unfinished')
    };
    const finishingName = unitFormData.unitFinishing ? finishingMap[unitFormData.unitFinishing] : '';

    const unitData = {
      ...unitFormData,
      id: unitFormData.id, // Preserve original ID for editing
      unitTypeName: unitType ? (unitType.nameEng || unitType.NameEng) : '',
      unitUsageName: unitUsage ? (unitUsage.nameEng || unitUsage.NameEng) : '',
      unitDirectionName: directionName,
      unitFinishingName: finishingName
    };

    if (editingUnitIndex !== null) {
      // Update existing unit - preserve the original ID
      const updatedUnits = [...units];
      updatedUnits[editingUnitIndex] = unitData;
      setUnits(updatedUnits);
      setEditingUnitIndex(null); // Reset editing index
    } else {
      // Add new unit
      setUnits([...units, unitData]);
    }
    setShowUnitModal(false);
  };

  const handleDeleteUnit = (index) => {
    const unitToRemove = units[index];
    // Track deleted unit ID if it's an existing unit (has an ID > 0)
    if (unitToRemove && unitToRemove.id && unitToRemove.id > 0) {
      setDeletedUnitIds(prev => [...prev, unitToRemove.id]);
    }
    setUnits(units.filter((_, i) => i !== index));
  };

  const handleUnitFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUnitFormData({
      ...unitFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Attachment handlers
  const handleAddAttachment = () => {
    setEditingAttachmentIndex(null);
    setAttachmentFormData({
      name: '',
      file: null,
      filePath: ''
    });
    setShowAttachmentModal(true);
  };

  const handleEditAttachment = (index) => {
    const attachment = attachments[index];
    setEditingAttachmentIndex(index);
    setAttachmentFormData({
      name: attachment.name,
      file: attachment.file || null,
      filePath: attachment.filePath || ''
    });
    setShowAttachmentModal(true);
  };

  const handleSaveAttachment = () => {
    if (!attachmentFormData.name.trim()) {
      setErrorMessage(t('property.create.messages.attachmentNameRequired'));
      setShowErrorModal(true);
      return;
    }

    if (!attachmentFormData.file && !attachmentFormData.filePath && editingAttachmentIndex === null) {
      setErrorMessage(t('property.create.messages.attachmentFileRequired'));
      setShowErrorModal(true);
      return;
    }

    const attachmentData = {
      name: attachmentFormData.name,
      file: attachmentFormData.file,
      filePath: attachmentFormData.filePath
    };

    if (editingAttachmentIndex !== null) {
      // Update existing attachment
      const updatedAttachments = [...attachments];
      updatedAttachments[editingAttachmentIndex] = attachmentData;
      setAttachments(updatedAttachments);
    } else {
      // Add new attachment
      setAttachments([...attachments, attachmentData]);
    }
    setShowAttachmentModal(false);
  };

  const handleDeleteAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (attachment.file) {
        // For new files not yet uploaded, create blob URL and download
        const url = URL.createObjectURL(attachment.file);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (attachment.filePath) {
        // For existing files, download from server
        const baseUrl = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com';
        const fileUrl = `${baseUrl.replace('/api', '')}${attachment.filePath}`;
        
        // Fetch the file as blob to force download
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setErrorMessage(t('property.create.messages.downloadAttachmentError'));
      setShowErrorModal(true);
    }
  };

  const handleViewAttachment = (attachment) => {
    try {
      if (attachment.file) {
        // For new files not yet uploaded, create blob URL and open
        const url = URL.createObjectURL(attachment.file);
        window.open(url, '_blank');
        // Clean up after a delay
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else if (attachment.filePath) {
        // For existing files, open from server
        const baseUrl = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com';
        const fileUrl = `${baseUrl.replace('/api', '')}${attachment.filePath}`;
        window.open(fileUrl, '_blank');
      }
    } catch (err) {
      setErrorMessage(t('property.create.messages.viewAttachmentError'));
      setShowErrorModal(true);
    }
  };

  const handleAttachmentFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setAttachmentFormData({
        ...attachmentFormData,
        file: files && files.length > 0 ? files[0] : null
      });
    } else {
      setAttachmentFormData({
        ...attachmentFormData,
        [name]: value
      });
    }
  };


  return (
    <div className="page-container">
      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
          >
            <i className="fas fa-info-circle"></i>
            <span>{t('property.create.tabs.propertyInformation')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
          >
            <i className="fas fa-users"></i>
            <span>{t('property.create.tabs.ownershipInformation')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
          >
            <i className="fas fa-building"></i>
            <span>{t('property.create.tabs.propertyDetails')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 4 ? 'active' : ''} ${!canAccessTab(4) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(4)}
          >
            <i className="fas fa-door-open"></i>
            <span>{t('property.create.tabs.unitsDetails')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 5 ? 'active' : ''} ${!canAccessTab(5) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(5)}
          >
            <i className="fas fa-paperclip"></i>
            <span>{t('property.create.tabs.attachments')}</span>
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          {/* Tab 1: Property Information */}
          {activeTab === 1 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.create.tabs.propertyInformation')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('property.create.fields.tab1.nameEng')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameEng ? 'is-invalid' : ''}`}
                    name="nameEng"
                    value={tab1Data.nameEng}
                    onChange={handleTab1Change}
                    placeholder={t('property.create.fields.tab1.placeholders.nameEng')}
                  />
                  {errors.nameEng && <div className="error-message">{errors.nameEng}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('property.create.fields.tab1.nameAr')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameAr ? 'is-invalid' : ''}`}
                    name="nameAr"
                    value={tab1Data.nameAr}
                    onChange={handleTab1Change}
                    placeholder={t('property.create.fields.tab1.placeholders.nameAr')}
                  />
                  {errors.nameAr && <div className="error-message">{errors.nameAr}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> {t('property.create.fields.tab1.ownerDocumentType')} *
                  </label>
                  <select
                    className={`form-input ${errors.ownerDocumentType ? 'is-invalid' : ''}`}
                    name="ownerDocumentType"
                    value={tab1Data.ownerDocumentType}
                    onChange={handleTab1Change}
                  >
                    <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                    <option value="1">{t('property.create.fields.tab1.options.electronicTitleDeed')}</option>
                    <option value="2">{t('property.create.fields.tab1.options.propertyRegistryTitleDeed')}</option>
                  </select>
                  {errors.ownerDocumentType && <div className="error-message">{errors.ownerDocumentType}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab1.titleDeed')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.titleDeed ? 'is-invalid' : ''}`}
                    name="titleDeed"
                    value={tab1Data.titleDeed}
                    onChange={handleTab1Change}
                    placeholder={t('property.create.fields.tab1.placeholders.titleDeed')}
                  />
                  {errors.titleDeed && <div className="error-message">{errors.titleDeed}</div>}
                </div>

                {tab1Data.ownerDocumentType === '1' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-id-card"></i> {t('property.create.fields.tab1.identityNumber')} *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.identityNumber ? 'is-invalid' : ''}`}
                      name="identityNumber"
                      value={tab1Data.identityNumber}
                      onChange={handleTab1Change}
                      placeholder={t('property.create.fields.tab1.placeholders.identityNumber')}
                    />
                    {errors.identityNumber && <div className="error-message">{errors.identityNumber}</div>}
                  </div>
                )}

                {tab1Data.ownerDocumentType === '2' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-building"></i> {t('property.create.fields.tab1.crNumber')} *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.crNumber ? 'is-invalid' : ''}`}
                      name="crNumber"
                      value={tab1Data.crNumber}
                      onChange={handleTab1Change}
                      placeholder={t('property.create.fields.tab1.placeholders.crNumber')}
                    />
                    {errors.crNumber && <div className="error-message">{errors.crNumber}</div>}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('property.create.fields.tab1.issueDate')} *
                  </label>
                  <input
                    type="date"
                    className={`form-input ${errors.issueDate ? 'is-invalid' : ''}`}
                    name="issueDate"
                    value={tab1Data.issueDate}
                    onChange={handleTab1Change}
                  />
                  {errors.issueDate && <div className="error-message">{errors.issueDate}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marker-alt"></i> {t('property.create.fields.tab1.placeTypeCity')} *
                  </label>
                  <select
                    className={`form-input ${errors.placeTypeCityId ? 'is-invalid' : ''}`}
                    name="placeTypeCityId"
                    value={tab1Data.placeTypeCityId}
                    onChange={handleTab1Change}
                  >
                    <option value="">-- {t('property.create.fields.tab1.selectCity')} --</option>
                    {(dropdowns.cities || []).map(city => (
                      <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                    ))}
                  </select>
                  {errors.placeTypeCityId && <div className="error-message">{errors.placeTypeCityId}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-code-branch"></i> {t('property.create.fields.tab1.branch')} *
                  </label>
                  <div className={errors.branchId ? 'is-invalid' : ''}>
                    <BranchSearchableSelect
                      branches={dropdowns.branches || []}
                      value={tab1Data.branchId}
                      onChange={(branchId) => {
                        setTab1Data({...tab1Data, branchId: branchId || ''});
                        if (errors.branchId) {
                          setErrors({...errors, branchId: ''});
                        }
                      }}
                      placeholder={t('property.create.fields.tab1.selectBranch')}
                    />
                  </div>
                  {errors.branchId && <div className="error-message">{errors.branchId}</div>}
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Ownership Information */}
          {activeTab === 2 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.create.tabs.ownershipInformation')}</h3>
              <div className="tab-actions">
                <button type="button" className="btn btn-primary" onClick={handleAddOwner}>
                  <i className="fas fa-plus"></i> {t('property.create.fields.tab2.addOwner')}
                </button>
                <button type="button" className="btn btn-info" onClick={handleAddAgent}>
                  <i className="fas fa-user-tie"></i> {t('property.create.fields.tab2.addAgent')}
                </button>
              </div>
              <div className="ownership-table-container">
                {ownerships.length === 0 ? (
                  <div className="empty-state">{t('property.create.messages.noOwnership')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.create.fields.tab2.type')}</th>
                        <th>{t('property.create.fields.tab2.nameAr')}</th>
                        <th>{t('property.create.fields.tab2.nameEng')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerships.map((owner, index) => (
                        <tr key={index}>
                          <td>{owner.isAgent ? t('property.create.fields.tab2.agent') : t('property.create.fields.tab2.owner')}</td>
                          <td>{owner.ownershipNameAr}</td>
                          <td>{owner.ownershipNameEng}</td>
                          <td>
                            <button className="btn-icon" onClick={() => handleEditOwnership(index)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn-icon" onClick={() => handleDeleteOwnership(index)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab 3: Property Details */}
          {activeTab === 3 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.create.tabs.propertyDetails')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> {t('property.create.fields.tab3.ownerDocumentType')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="ownerDocumentType"
                      value={tab1Data.ownerDocumentType}
                      onChange={(e) => {
                        setTab1Data({...tab1Data, [e.target.name]: e.target.value});
                      }}
                    >
                      <option value="1">{t('property.create.fields.tab1.options.titleDeed')}</option>
                      <option value="2">{t('property.create.fields.tab1.options.leaseAgreement')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab3.titleDeedNumbers')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="titleDeed"
                      value={tab1Data.titleDeed}
                      onChange={(e) => {
                        setTab1Data({...tab1Data, [e.target.name]: e.target.value});
                      }}
                      placeholder={t('property.create.fields.tab3.placeholders.titleDeed')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-certificate"></i> {t('property.create.fields.tab3.municipalLicenseNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-certificate input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.municipalLicenseNumber.trim() ? 'is-invalid' : ''}`}
                      name="municipalLicenseNumber"
                      value={tab3Data.municipalLicenseNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.municipalLicense')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.municipalLicenseNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.municipalLicenseRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.propertyType')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.realEstateTypeId ? 'is-invalid' : ''}`}
                      name="realEstateTypeId"
                      value={tab3Data.realEstateTypeId}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateTypes || []).map(type => (
                        <option key={type.id} value={type.id}>{type.nameEng || type.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.realEstateTypeId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.propertyTypeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-info-circle"></i> {t('property.create.fields.tab3.propertyStatus')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-info-circle input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.realEstateStatus ? 'is-invalid' : ''}`}
                      name="realEstateStatus"
                      value={tab3Data.realEstateStatus}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      <option value="1">{t('property.create.fields.tab3.options.rent')}</option>
                      <option value="2">{t('property.create.fields.tab3.options.booked')}</option>
                      <option value="3">{t('property.create.fields.tab3.options.available')}</option>
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.realEstateStatus && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.propertyStatusRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tag"></i> {t('property.create.fields.tab3.propertyUsage')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tag input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.realEstateUsageId ? 'is-invalid' : ''}`}
                      name="realEstateUsageId"
                      value={tab3Data.realEstateUsageId}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateUsages || []).map(usage => (
                        <option key={usage.id} value={usage.id}>{usage.nameEng || usage.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.realEstateUsageId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.propertyUsageRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab3.propertyNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.realEstateNumber.trim() ? 'is-invalid' : ''}`}
                      name="realEstateNumber"
                      value={tab3Data.realEstateNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.propertyNumber')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.realEstateNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.propertyNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-layer-group"></i> {t('property.create.fields.tab3.unitFloor')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-layer-group input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.unitFloor ? 'is-invalid' : ''}`}
                      name="unitFloor"
                      value={tab3Data.unitFloor}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.unitFloor')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.unitFloor && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitFloorRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar-alt"></i> {t('property.create.fields.tab3.propertyAge')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar-alt input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.realEstateAge ? 'is-invalid' : ''}`}
                      name="realEstateAge"
                      value={tab3Data.realEstateAge}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.age')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.realEstateAge && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.propertyAgeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calculator"></i> {t('property.create.fields.tab3.unitTotalNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calculator input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.unitTotalNumber ? 'is-invalid' : ''}`}
                      name="unitTotalNumber"
                      value={tab3Data.unitTotalNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.totalUnits')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.unitTotalNumber && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitTotalNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.floorsTotalNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.floorsTotalNumber ? 'is-invalid' : ''}`}
                      name="floorsTotalNumber"
                      value={tab3Data.floorsTotalNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.totalFloors')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.floorsTotalNumber && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.floorsTotalNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-check-square"></i> {t('property.create.fields.tab3.isPartOfCompound')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-check-square input-icon"></i>
                    <select
                      className="form-input"
                      name="isPartOfCompound"
                      value={tab3Data.isPartOfCompound ? 'true' : 'false'}
                      onChange={(e) => setTab3Data({...tab3Data, isPartOfCompound: e.target.value === 'true'})}
                    >
                      <option value="true">{t('common.yes')}</option>
                      <option value="false">{t('common.no')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> {t('property.create.fields.tab3.area')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.areaId ? 'is-invalid' : ''}`}
                      name="areaId"
                      value={tab3Data.areaId}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.areaId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.areaRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('property.create.fields.tab3.city')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.cityId ? 'is-invalid' : ''}`}
                      name="cityId"
                      value={tab3Data.cityId}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.cityId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.cityRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('property.create.fields.tab3.district')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className={`form-input ${tab3FormSubmitted && !tab3Data.districtId ? 'is-invalid' : ''}`}
                      name="districtId"
                      value={tab3Data.districtId}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {tab3FormSubmitted && !tab3Data.districtId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.districtRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('property.create.fields.tab3.streetName')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.streetName.trim() ? 'is-invalid' : ''}`}
                      name="streetName"
                      value={tab3Data.streetName}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.streetName')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.streetName.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.streetNameRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.buildingNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.buildingNumber.trim() ? 'is-invalid' : ''}`}
                      name="buildingNumber"
                      value={tab3Data.buildingNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.buildingNumber')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.buildingNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.buildingNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus"></i> {t('property.create.fields.tab3.additionalNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${tab3FormSubmitted && !tab3Data.additionalNumber.trim() ? 'is-invalid' : ''}`}
                      name="additionalNumber"
                      value={tab3Data.additionalNumber}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.additionalNumber')}
                    />
                  </div>
                  {tab3FormSubmitted && !tab3Data.additionalNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.additionalNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('property.create.fields.tab3.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={tab3Data.postalCode}
                      onChange={(e) => setTab3Data({...tab3Data, [e.target.name]: e.target.value})}
                      placeholder={t('property.create.fields.tab3.placeholders.postalCode')}
                    />
                  </div>
                </div>
              </div>

              {/* Facilities and Amenities Section */}
              <div className="facilities-section">
                <label className="form-label facilities-section-label">
                  <i className="fas fa-star"></i> {t('property.create.fields.tab3.containsFacilities')}
                </label>
                <div className="facilities-section-container">
                  <div className="facilities-grid">
                  {(dropdowns.facilities || []).map(facility => {
                    const isChecked = tab3Data.selectedFacilities[facility.id] !== undefined;
                    const quantity = tab3Data.selectedFacilities[facility.id] || 1;
                    return (
                      <div
                        key={facility.id}
                        className={`facility-card ${isChecked ? 'active' : ''}`}
                        onClick={() => {
                          const newFacilities = { ...tab3Data.selectedFacilities };
                          if (isChecked) {
                            delete newFacilities[facility.id];
                          } else {
                            newFacilities[facility.id] = 1;
                          }
                          setTab3Data({ ...tab3Data, selectedFacilities: newFacilities });
                        }}
                      >
                        <div className="facility-card-content">
                          <div className="facility-icon">
                            <i className="fas fa-check-circle"></i>
                          </div>
                          <div className="facility-info">
                            <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                            {facility.nameAr || facility.NameAr ? (
                              <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                            ) : null}
                          </div>
                          <div className="facility-quantity-select">
                            <select
                              className="form-select-sm facility-quantity-dropdown"
                              value={quantity}
                              disabled={!isChecked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const newFacilities = { ...tab3Data.selectedFacilities };
                                newFacilities[facility.id] = parseInt(e.target.value);
                                setTab3Data({ ...tab3Data, selectedFacilities: newFacilities });
                              }}
                            >
                              {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="facility-checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Units Details */}
          {activeTab === 4 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.create.tabs.unitsDetails')}</h3>
              <div className="tab-actions">
                <button type="button" className="btn btn-primary" onClick={handleAddUnit}>
                  <i className="fas fa-plus"></i> {t('property.create.fields.tables.units.addNewUnit')}
                </button>
              </div>
              <div className="units-table-container">
                {units.length === 0 ? (
                  <div className="empty-state">{t('property.create.fields.tables.units.noUnits')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.create.fields.tables.units.unitNumber')}</th>
                        <th>{t('property.create.fields.tables.units.unitType')}</th>
                        <th>{t('property.create.fields.tables.units.unitUsage')}</th>
                        <th>{t('property.create.fields.tables.units.floor')}</th>
                        <th>{t('property.create.fields.tables.units.area')}</th>
                        <th>{t('property.create.fields.tables.units.rooms')}</th>
                        <th>{t('property.create.fields.tables.units.estDate')}</th>
                        <th>{t('property.create.fields.tables.units.direction')}</th>
                        <th>{t('property.create.fields.tables.units.finishing')}</th>
                        <th>{t('property.create.fields.tables.units.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit, index) => {
                        // Look up unit type and usage names from dropdowns if not already set
                        const unitTypeName = unit.unitTypeName || (() => {
                          const unitType = (dropdowns.unitTypes || []).find(t => (t.id || t.Id) === parseInt(unit.unitTypeId));
                          return unitType ? (unitType.nameEng || unitType.NameEng) : '';
                        })();
                        const unitUsageName = unit.unitUsageName || (() => {
                          const unitUsage = (dropdowns.unitUsages || []).find(u => (u.id || u.Id) === parseInt(unit.unitUsageId));
                          return unitUsage ? (unitUsage.nameEng || unitUsage.NameEng) : '';
                        })();
                        
                        return (
                        <tr key={index}>
                          <td>{unit.unitNumber || '-'}</td>
                          <td>{unitTypeName || '-'}</td>
                          <td>{unitUsageName || '-'}</td>
                          <td>{unit.floorNumber || '-'}</td>
                          <td>{unit.unitArea || '-'}</td>
                          <td>{unit.numberOfRooms || '-'}</td>
                          <td>{unit.establishedDate || '-'}</td>
                          <td>{unit.unitDirectionName || unit.unitDirection || '-'}</td>
                          <td>{unit.unitFinishingName || unit.unitFinishing || '-'}</td>
                          <td>
                            <button className="btn-icon" onClick={() => handleEditUnit(index)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn-icon" onClick={() => handleDeleteUnit(index)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab 5: Attachments */}
          {activeTab === 5 && (
            <div className="tab-panel">
              <div className="tab-actions" style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                <button type="button" className="btn btn-primary" onClick={handleAddAttachment}>
                  <i className="fas fa-plus"></i> {t('property.create.fields.tab5.addNewAttachment')}
                </button>
                <h3 className="tab-title" style={{ margin: 0 }}>{t('property.create.tabs.attachments')}</h3>
              </div>
              <div className="attachments-table-container">
                {attachments.length === 0 ? (
                  <div className="empty-state">{t('property.create.messages.noAttachments')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.create.fields.tab5.attachmentName')}</th>
                        <th>{t('property.create.fields.tab5.uploadFile')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((attachment, index) => (
                        <tr key={index}>
                          <td>{attachment.name}</td>
                          <td>
                            {attachment.file ? (
                              <span className="text-info">
                                <i className="fas fa-file"></i> {attachment.file.name}
                                {attachment.file.size && ` (${(attachment.file.size / 1024).toFixed(2)} KB)`}
                              </span>
                            ) : attachment.filePath ? (
                              <span className="text-success">
                                <i className="fas fa-check-circle"></i> {t('property.create.messages.fileUploaded')}
                              </span>
                            ) : (
                              <span className="text-warning">{t('property.create.messages.noFileSelected')}</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              {(attachment.file || attachment.filePath) && (
                                <>
                                  <button 
                                    className="btn-icon" 
                                    onClick={() => handleViewAttachment(attachment)}
                                    title={t('common.view')}
                                  >
                                    <i className="fas fa-eye" style={{ color: '#17a2b8' }}></i>
                                  </button>
                                  <button 
                                    className="btn-icon" 
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    title={t('property.create.buttons.download')}
                                  >
                                    <i className="fas fa-download" style={{ color: '#28a745' }}></i>
                                  </button>
                                </>
                              )}
                              <button 
                                className="btn-icon" 
                                onClick={() => handleEditAttachment(index)}
                                title={t('common.edit')}
                              >
                                <i className="fas fa-edit" style={{ color: '#ff9800' }}></i>
                              </button>
                              <button 
                                className="btn-icon" 
                                onClick={() => handleDeleteAttachment(index)}
                                title={t('common.delete')}
                              >
                                <i className="fas fa-trash" style={{ color: '#dc3545' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="tab-navigation">
          <button
            type="button"
            className="btn btn-secondary nav-btn"
            onClick={handlePrevious}
            disabled={activeTab === 1 || saving}
          >
            {t('property.create.buttons.previous')}
          </button>
          {activeTab < 5 ? (
            <button
              type="button"
              className="btn btn-primary nav-btn"
              onClick={handleNext}
              disabled={saving}
            >
              {saving ? t('property.create.buttons.saving') : t('property.create.buttons.next')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-success nav-btn"
              onClick={handleFinish}
              disabled={saving}
            >
              {saving ? t('property.create.buttons.saving') : t('property.create.buttons.save')}
            </button>
          )}
        </div>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('property.create.messages.createSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      {/* Ownership Modal */}
      {showOwnershipModal && (
        <div className="modal-overlay" onClick={() => setShowOwnershipModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-user"></i> {editingOwnershipIndex !== null ? t('property.create.fields.modals.owner.editTitle') : t('property.create.fields.modals.owner.title')}
              </h3>
              <button className="modal-close" onClick={() => setShowOwnershipModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('property.create.fields.modals.owner.ownershipNameAr')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.ownershipNameAr.trim() ? 'is-invalid' : ''}`}
                      name="ownershipNameAr"
                      value={ownershipFormData.ownershipNameAr}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.ownershipNameAr')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.ownershipNameAr.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.ownershipNameArRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('property.create.fields.modals.owner.ownershipNameEng')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.ownershipNameEng.trim() ? 'is-invalid' : ''}`}
                      name="ownershipNameEng"
                      value={ownershipFormData.ownershipNameEng}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.ownershipNameEng')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.ownershipNameEng.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.ownershipNameEngRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('property.create.fields.modals.owner.dateOfBirth')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.dob ? 'is-invalid' : ''}`}
                      name="dob"
                      value={ownershipFormData.dob}
                      onChange={handleOwnershipFormChange}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.dob && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.dobRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> {t('property.create.fields.modals.owner.nationality')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.nationalityId ? 'is-invalid' : ''}`}
                      name="nationalityId"
                      value={ownershipFormData.nationalityId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.id}>{nat.nationalityNameEng || nat.NationalityNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.nationalityId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.nationalityRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> {t('property.create.fields.modals.owner.identityType')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.identityTypeId ? 'is-invalid' : ''}`}
                      name="identityTypeId"
                      value={ownershipFormData.identityTypeId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.identities || []).map(id => {
                        const name = id.IdentityNameEng || id.identityNameEng || id.nameEng || id.NameEng || '';
                        return (
                          <option key={id.id} value={id.id}>{name}</option>
                        );
                      })}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.identityTypeId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.identityTypeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.modals.owner.identityNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.identityNumber.trim() ? 'is-invalid' : ''}`}
                      name="identityNumber"
                      value={ownershipFormData.identityNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.identityNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.identityNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.identityNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> {t('property.create.fields.modals.owner.mobileCountryCode')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.mobileNumberCountryCode ? 'is-invalid' : ''}`}
                      name="mobileNumberCountryCode"
                      value={ownershipFormData.mobileNumberCountryCode}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.selectCountry')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.phoneCode || nat.PhoneCode}>
                          {nat.phoneCode || nat.PhoneCode} - {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.mobileNumberCountryCode && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.mobileCountryCodeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> {t('property.create.fields.modals.owner.mobileNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.mobileNumber.trim() ? 'is-invalid' : ''}`}
                      name="mobileNumber"
                      value={ownershipFormData.mobileNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.mobileNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.mobileNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.mobileNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> {t('property.create.fields.modals.owner.email')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.emailId.trim() ? 'is-invalid' : ''}`}
                      name="emailId"
                      value={ownershipFormData.emailId}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.email')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.emailId.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.emailRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> {t('property.create.fields.modals.owner.bank')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.bankId ? 'is-invalid' : ''}`}
                      name="bankId"
                      value={ownershipFormData.bankId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.nameEng || bank.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.bankId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.bankRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-credit-card"></i> {t('property.create.fields.modals.owner.bankAccountNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-credit-card input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.bankAccountNumber.trim() ? 'is-invalid' : ''}`}
                      name="bankAccountNumber"
                      value={ownershipFormData.bankAccountNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.bankAccountNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.bankAccountNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.bankAccountRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> {t('property.create.fields.modals.owner.area')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.areaId ? 'is-invalid' : ''}`}
                      name="areaId"
                      value={ownershipFormData.areaId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.areaId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.areaRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('property.create.fields.modals.owner.city')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.cityId ? 'is-invalid' : ''}`}
                      name="cityId"
                      value={ownershipFormData.cityId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.cityId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.cityRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('property.create.fields.modals.owner.district')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.districtId ? 'is-invalid' : ''}`}
                      name="districtId"
                      value={ownershipFormData.districtId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.owner.selectOptions.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.districtId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.districtRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('property.create.fields.modals.owner.streetName')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.streetName.trim() ? 'is-invalid' : ''}`}
                      name="streetName"
                      value={ownershipFormData.streetName}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.streetName')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.streetName.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.streetNameRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.modals.owner.buildingNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.buildingNumber.trim() ? 'is-invalid' : ''}`}
                      name="buildingNumber"
                      value={ownershipFormData.buildingNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.buildingNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.buildingNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.buildingNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus-square"></i> {t('property.create.fields.modals.owner.additionalNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus-square input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.additionalNumber.trim() ? 'is-invalid' : ''}`}
                      name="additionalNumber"
                      value={ownershipFormData.additionalNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.additionalNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.additionalNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.additionalNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('property.create.fields.modals.owner.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={ownershipFormData.postalCode}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.owner.placeholders.postalCode')}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowOwnershipModal(false)}>
                {t('property.create.buttons.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveOwnership}>
                {t('property.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Modal */}
      {showAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAgentModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-user-tie"></i> {editingOwnershipIndex !== null ? t('property.create.fields.modals.agent.editTitle') : t('property.create.fields.modals.agent.title')}
              </h3>
              <button className="modal-close" onClick={() => setShowAgentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-badge"></i> {t('property.create.fields.modals.agent.agentNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-badge input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.agentNumber.trim() ? 'is-invalid' : ''}`}
                      name="agentNumber"
                      value={ownershipFormData.agentNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.agentNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.agentNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.agentNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('property.create.fields.modals.agent.ownershipNameAr')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.ownershipNameAr.trim() ? 'is-invalid' : ''}`}
                      name="ownershipNameAr"
                      value={ownershipFormData.ownershipNameAr}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.ownershipNameAr')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.ownershipNameAr.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.ownershipNameArRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('property.create.fields.modals.agent.ownershipNameEng')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.ownershipNameEng.trim() ? 'is-invalid' : ''}`}
                      name="ownershipNameEng"
                      value={ownershipFormData.ownershipNameEng}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.ownershipNameEng')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.ownershipNameEng.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.ownershipNameEngRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('property.create.fields.modals.agent.dateOfBirth')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.dob ? 'is-invalid' : ''}`}
                      name="dob"
                      value={ownershipFormData.dob}
                      onChange={handleOwnershipFormChange}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.dob && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.dobRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> {t('property.create.fields.modals.agent.nationality')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.nationalityId ? 'is-invalid' : ''}`}
                      name="nationalityId"
                      value={ownershipFormData.nationalityId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.id}>{nat.nationalityNameEng || nat.NationalityNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.nationalityId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.nationalityRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> {t('property.create.fields.modals.agent.identityType')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.identityTypeId ? 'is-invalid' : ''}`}
                      name="identityTypeId"
                      value={ownershipFormData.identityTypeId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.identities || []).map(id => {
                        const name = id.IdentityNameEng || id.identityNameEng || id.nameEng || id.NameEng || '';
                        return (
                          <option key={id.id} value={id.id}>{name}</option>
                        );
                      })}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.identityTypeId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.identityTypeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.modals.agent.identityNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.identityNumber.trim() ? 'is-invalid' : ''}`}
                      name="identityNumber"
                      value={ownershipFormData.identityNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.identityNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.identityNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.identityNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> {t('property.create.fields.modals.agent.mobileCountryCode')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.mobileNumberCountryCode ? 'is-invalid' : ''}`}
                      name="mobileNumberCountryCode"
                      value={ownershipFormData.mobileNumberCountryCode}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.selectCountry')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.phoneCode || nat.PhoneCode}>
                          {nat.phoneCode || nat.PhoneCode} - {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.mobileNumberCountryCode && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.mobileCountryCodeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> {t('property.create.fields.modals.agent.mobileNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.mobileNumber.trim() ? 'is-invalid' : ''}`}
                      name="mobileNumber"
                      value={ownershipFormData.mobileNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.mobileNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.mobileNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.mobileNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> {t('property.create.fields.modals.agent.email')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.emailId.trim() ? 'is-invalid' : ''}`}
                      name="emailId"
                      value={ownershipFormData.emailId}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.email')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.emailId.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.emailRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> {t('property.create.fields.modals.agent.bank')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.bankId ? 'is-invalid' : ''}`}
                      name="bankId"
                      value={ownershipFormData.bankId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.nameEng || bank.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.bankId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.bankRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-credit-card"></i> {t('property.create.fields.modals.agent.bankAccountNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-credit-card input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.bankAccountNumber.trim() ? 'is-invalid' : ''}`}
                      name="bankAccountNumber"
                      value={ownershipFormData.bankAccountNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.bankAccountNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.bankAccountNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.bankAccountRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> {t('property.create.fields.modals.agent.area')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.areaId ? 'is-invalid' : ''}`}
                      name="areaId"
                      value={ownershipFormData.areaId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.areaId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.areaRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('property.create.fields.modals.agent.city')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.cityId ? 'is-invalid' : ''}`}
                      name="cityId"
                      value={ownershipFormData.cityId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.cityId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.cityRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('property.create.fields.modals.agent.district')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.districtId ? 'is-invalid' : ''}`}
                      name="districtId"
                      value={ownershipFormData.districtId}
                      onChange={handleOwnershipFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.agent.selectOptions.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.districtId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.districtRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('property.create.fields.modals.agent.streetName')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.streetName.trim() ? 'is-invalid' : ''}`}
                      name="streetName"
                      value={ownershipFormData.streetName}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.streetName')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.streetName.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.streetNameRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.modals.agent.buildingNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.buildingNumber.trim() ? 'is-invalid' : ''}`}
                      name="buildingNumber"
                      value={ownershipFormData.buildingNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.buildingNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.buildingNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.buildingNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus-square"></i> {t('property.create.fields.modals.agent.additionalNumber')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus-square input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${ownershipFormSubmitted && !ownershipFormData.additionalNumber.trim() ? 'is-invalid' : ''}`}
                      name="additionalNumber"
                      value={ownershipFormData.additionalNumber}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.additionalNumber')}
                    />
                  </div>
                  {ownershipFormSubmitted && !ownershipFormData.additionalNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.additionalNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('property.create.fields.modals.agent.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={ownershipFormData.postalCode}
                      onChange={handleOwnershipFormChange}
                      placeholder={t('property.create.fields.modals.agent.placeholders.postalCode')}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAgentModal(false)}>
                {t('property.create.buttons.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveOwnership}>
                {t('property.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Modal */}
      {showUnitModal && (
        <div className="modal-overlay" onClick={() => setShowUnitModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-home"></i> {editingUnitIndex !== null ? t('property.create.fields.modals.unit.editTitle') : t('property.create.fields.modals.unit.title')}
              </h3>
              <button className="modal-close" onClick={() => setShowUnitModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.modals.unit.unitNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitNumber.trim() ? 'is-invalid' : ''}`}
                      name="unitNumber"
                      value={unitFormData.unitNumber}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.unitNumber')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.unitNumber.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.modals.unit.unitType')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <select
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitTypeId ? 'is-invalid' : ''}`}
                      name="unitTypeId"
                      value={unitFormData.unitTypeId}
                      onChange={handleUnitFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.unit.selectOptions.select')} --</option>
                      {(dropdowns.unitTypes || []).map(type => (
                        <option key={type.id} value={type.id}>{type.nameEng || type.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {unitFormSubmitted && !unitFormData.unitTypeId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitTypeRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tag"></i> {t('property.create.fields.modals.unit.unitUsage')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tag input-icon"></i>
                    <select
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitUsageId ? 'is-invalid' : ''}`}
                      name="unitUsageId"
                      value={unitFormData.unitUsageId}
                      onChange={handleUnitFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.unit.selectOptions.select')} --</option>
                      {(dropdowns.unitUsages || []).map(usage => (
                        <option key={usage.id} value={usage.id}>{usage.nameEng || usage.NameEng}</option>
                      ))}
                    </select>
                  </div>
                  {unitFormSubmitted && !unitFormData.unitUsageId && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitUsageRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-layer-group"></i> {t('property.create.fields.modals.unit.floorNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-layer-group input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${unitFormSubmitted && !unitFormData.floorNumber ? 'is-invalid' : ''}`}
                      name="floorNumber"
                      value={unitFormData.floorNumber}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.floorNumber')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.floorNumber && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.floorNumberRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-combined"></i> {t('property.create.fields.modals.unit.unitArea')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-combined input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitArea ? 'is-invalid' : ''}`}
                      name="unitArea"
                      value={unitFormData.unitArea}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.unitArea')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.unitArea && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitAreaRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-door-open"></i> {t('property.create.fields.modals.unit.numberOfRooms')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-door-open input-icon"></i>
                    <input
                      type="number"
                      className={`form-input ${unitFormSubmitted && !unitFormData.numberOfRooms ? 'is-invalid' : ''}`}
                      name="numberOfRooms"
                      value={unitFormData.numberOfRooms}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.numberOfRooms')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.numberOfRooms && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.numberOfRoomsRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('property.create.fields.modals.unit.establishedDate')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className={`form-input ${unitFormSubmitted && !unitFormData.establishedDate ? 'is-invalid' : ''}`}
                      name="establishedDate"
                      value={unitFormData.establishedDate}
                      onChange={handleUnitFormChange}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.establishedDate && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.establishedDateRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-compass"></i> {t('property.create.fields.modals.unit.unitDirection')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-compass input-icon"></i>
                    <select
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitDirection ? 'is-invalid' : ''}`}
                      name="unitDirection"
                      value={unitFormData.unitDirection}
                      onChange={handleUnitFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.unit.selectOptions.select')} --</option>
                      <option value="1">{t('property.create.fields.modals.unit.directions.north')}</option>
                      <option value="2">{t('property.create.fields.modals.unit.directions.south')}</option>
                      <option value="3">{t('property.create.fields.modals.unit.directions.east')}</option>
                      <option value="4">{t('property.create.fields.modals.unit.directions.west')}</option>
                      <option value="5">{t('property.create.fields.modals.unit.directions.northEast')}</option>
                      <option value="6">{t('property.create.fields.modals.unit.directions.northWest')}</option>
                      <option value="7">{t('property.create.fields.modals.unit.directions.southEast')}</option>
                      <option value="8">{t('property.create.fields.modals.unit.directions.southWest')}</option>
                    </select>
                  </div>
                  {unitFormSubmitted && !unitFormData.unitDirection && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitDirectionRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-paint-brush"></i> {t('property.create.fields.modals.unit.unitFinishing')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-paint-brush input-icon"></i>
                    <select
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitFinishing ? 'is-invalid' : ''}`}
                      name="unitFinishing"
                      value={unitFormData.unitFinishing}
                      onChange={handleUnitFormChange}
                    >
                      <option value="">-- {t('property.create.fields.modals.unit.selectOptions.select')} --</option>
                      <option value="1">{t('property.create.fields.modals.unit.finishing.basic')}</option>
                      <option value="2">{t('property.create.fields.modals.unit.finishing.standard')}</option>
                      <option value="3">{t('property.create.fields.modals.unit.finishing.premium')}</option>
                      <option value="4">{t('property.create.fields.modals.unit.finishing.luxury')}</option>
                      <option value="5">{t('property.create.fields.modals.unit.finishing.unfinished')}</option>
                    </select>
                  </div>
                  {unitFormSubmitted && !unitFormData.unitFinishing && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitFinishingRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler"></i> {t('property.create.fields.modals.unit.unitFrontLength')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitFrontLength"
                      value={unitFormData.unitFrontLength}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.frontLength')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-horizontal"></i> {t('property.create.fields.modals.unit.unitLength')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-horizontal input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitLength"
                      value={unitFormData.unitLength}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.length')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-vertical"></i> {t('property.create.fields.modals.unit.unitWidth')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-vertical input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-input ${unitFormSubmitted && !unitFormData.unitWidth ? 'is-invalid' : ''}`}
                      name="unitWidth"
                      value={unitFormData.unitWidth}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.width')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.unitWidth && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.unitWidthRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-arrows-alt-v"></i> {t('property.create.fields.modals.unit.unitHeight')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-arrows-alt-v input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitHeight"
                      value={unitFormData.unitHeight}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.height')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-check-square"></i> {t('property.create.fields.modals.unit.includeMezzanine')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-check-square input-icon"></i>
                    <select
                      className="form-input"
                      name="includeMezzanine"
                      value={unitFormData.includeMezzanine ? 'true' : 'false'}
                      onChange={(e) => setUnitFormData({...unitFormData, includeMezzanine: e.target.value === 'true'})}
                    >
                      <option value="false">{t('property.create.fields.modals.unit.selectOptions.no')}</option>
                      <option value="true">{t('property.create.fields.modals.unit.selectOptions.yes')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-couch"></i> {t('property.create.fields.modals.unit.furnished')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-couch input-icon"></i>
                    <select
                      className="form-input"
                      name="furnished"
                      value={unitFormData.furnished ? 'true' : 'false'}
                      onChange={(e) => setUnitFormData({...unitFormData, furnished: e.target.value === 'true'})}
                    >
                      <option value="false">{t('property.create.fields.modals.unit.selectOptions.no')}</option>
                      <option value="true">{t('property.create.fields.modals.unit.selectOptions.yes')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-utensils"></i> {t('property.create.fields.modals.unit.kitchenCabinets')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-utensils input-icon"></i>
                    <select
                      className="form-input"
                      name="kitchenCabinets"
                      value={unitFormData.kitchenCabinets ? 'true' : 'false'}
                      onChange={(e) => setUnitFormData({...unitFormData, kitchenCabinets: e.target.value === 'true'})}
                    >
                      <option value="false">{t('property.create.fields.modals.unit.selectOptions.no')}</option>
                      <option value="true">{t('property.create.fields.modals.unit.selectOptions.yes')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-bolt"></i> {t('property.create.fields.modals.unit.electricityMeter')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-bolt input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${unitFormSubmitted && !unitFormData.electricityMeter.trim() ? 'is-invalid' : ''}`}
                      name="electricityMeter"
                      value={unitFormData.electricityMeter}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.electricityMeter')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.electricityMeter.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.electricityMeterRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-fire"></i> {t('property.create.fields.modals.unit.gasMeterNo')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-fire input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="gasMeterNo"
                      value={unitFormData.gasMeterNo}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.gasMeter')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tint"></i> {t('property.create.fields.modals.unit.waterMeter')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tint input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${unitFormSubmitted && !unitFormData.waterMeter.trim() ? 'is-invalid' : ''}`}
                      name="waterMeter"
                      value={unitFormData.waterMeter}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.waterMeter')}
                    />
                  </div>
                  {unitFormSubmitted && !unitFormData.waterMeter.trim() && <span className="validation-message"><i className="fas fa-exclamation-circle"></i> {t('property.create.errors.waterMeterRequired')}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-car"></i> {t('property.create.fields.modals.unit.countOfParking')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-car input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="countOfParking"
                      value={unitFormData.countOfParking}
                      onChange={handleUnitFormChange}
                      placeholder={t('property.create.fields.modals.unit.placeholders.parkingCount')}
                    />
                  </div>
                </div>
              </div>

              {/* Unit Facilities and Amenities Section */}
              <div className="facilities-section" style={{ marginTop: '2rem' }}>
                <label className="form-label facilities-section-label">
                  <i className="fas fa-star"></i> {t('property.create.fields.modals.unit.containsFacilities')}
                </label>
                <div className="facilities-section-container">
                  {(!dropdowns.unitFacilities || dropdowns.unitFacilities.length === 0) ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#bf4f1f' }}></i>
                      <p>{t('property.create.messages.noUnitFacilitiesAvailable')}</p>
                    </div>
                  ) : (
                  <div className="facilities-grid">
                    {dropdowns.unitFacilities.map(facility => {
                      const facilityId = parseInt(facility.id || facility.Id);
                      const isChecked = !isNaN(facilityId) && unitFormData.selectedFacilities[facilityId] !== undefined;
                      const quantity = isChecked ? unitFormData.selectedFacilities[facilityId] : 1;
                      return (
                        <div
                          key={facilityId}
                          className={`facility-card unit-facility-card ${isChecked ? 'active' : ''}`}
                          onClick={() => {
                            const newFacilities = { ...unitFormData.selectedFacilities };
                            if (isChecked) {
                              delete newFacilities[facilityId];
                            } else {
                              newFacilities[facilityId] = 1;
                            }
                            setUnitFormData({ ...unitFormData, selectedFacilities: newFacilities });
                          }}
                        >
                          <div className="facility-card-content">
                            <div className="facility-icon">
                              {facility.icon || facility.Icon ? (
                                <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                              ) : (
                                <i className="fas fa-check-circle"></i>
                              )}
                            </div>
                            <div className="facility-info">
                              <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                              {(facility.nameAr || facility.NameAr) && (
                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                              )}
                            </div>
                            <div className="facility-quantity-select">
                              <select
                                className="form-select-sm facility-quantity-dropdown"
                                value={quantity}
                                disabled={!isChecked}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newFacilities = { ...unitFormData.selectedFacilities };
                                  newFacilities[facilityId] = parseInt(e.target.value);
                                  setUnitFormData({ ...unitFormData, selectedFacilities: newFacilities });
                                }}
                              >
                                {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                  <option key={num} value={num}>{num}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            className="facility-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUnitModal(false)}>
                {t('property.create.buttons.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveUnit}>
                {t('property.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Modal */}
      {showAttachmentModal && (
        <div className="modal-overlay" onClick={() => setShowAttachmentModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ marginBottom: '1rem' }}>
              <h3 className="modal-title">
                <i className="fas fa-paperclip"></i> {editingAttachmentIndex !== null ? t('property.create.fields.tab5.editAttachment') : t('property.create.fields.tab5.addAttachment')}
              </h3>
              <button className="modal-close" onClick={() => setShowAttachmentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> {t('property.create.fields.tab5.attachmentName')} *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-alt input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={attachmentFormData.name}
                      onChange={handleAttachmentFormChange}
                      placeholder={t('property.create.fields.tab5.enterAttachmentName')}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-upload"></i> {t('property.create.fields.tab5.uploadFile')} {editingAttachmentIndex === null ? '*' : ''}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-upload input-icon"></i>
                    <input
                      type="file"
                      className="form-input"
                      name="file"
                      onChange={handleAttachmentFormChange}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                  </div>
                  {attachmentFormData.file && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> {attachmentFormData.file.name} ({(attachmentFormData.file.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                  {attachmentFormData.filePath && !attachmentFormData.file && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ color: '#17a2b8' }}></i> {t('property.create.messages.existingFileKept')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAttachmentModal(false)}>
                <i className="fas fa-times"></i> {t('property.create.buttons.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveAttachment}>
                <i className="fas fa-save"></i> {t('property.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable Branch Select Component
const BranchSearchableSelect = ({ branches, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const { t } = useTranslation();
  const selectRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedBranch = branches.find(b => String(b.id || b.Id) === String(value));
  const filteredBranches = branches.filter(b => {
    const search = searchTerm.toLowerCase();
    const nameAr = (b.branchNameAr || b.BranchNameAr || '').toLowerCase();
    const nameEng = (b.branchNameEng || b.BranchNameEng || '').toLowerCase();
    const code = (b.branchCode || b.BranchCode || '').toLowerCase();
    return nameAr.includes(search) || nameEng.includes(search) || code.includes(search);
  });

  return (
    <div className="searchable-select" ref={selectRef}>
      <div 
        className="searchable-select-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          readOnly
          value={selectedBranch ? (selectedBranch.branchNameAr || selectedBranch.BranchNameAr || selectedBranch.branchNameEng || selectedBranch.BranchNameEng) : ''}
          placeholder={placeholder}
          className="form-input"
        />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="searchable-select-options">
            {filteredBranches.length === 0 ? (
              <div className="searchable-select-no-results">{t('common.noData')}</div>
            ) : (
              filteredBranches.map(branch => (
                <div
                  key={branch.id || branch.Id}
                  className={`searchable-select-option ${String(branch.id || branch.Id) === String(value) ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(branch.id || branch.Id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {branch.branchNameAr || branch.BranchNameAr || branch.branchNameEng || branch.BranchNameEng} 
                  {(branch.branchCode || branch.BranchCode) && ` (${branch.branchCode || branch.BranchCode})`}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyCreate;
